/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
    afterNextRender,
    Component,
    computed,
    effect,
    EffectRef,
    ElementRef,
    OnDestroy,
    OnInit,
    signal,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {EditableGridComponent} from '../editable-grid-component';
import {
    CellEditingStoppedEvent,
    CellKeyDownEvent,
    ColDef,
    ColGroupDef,
    GridReadyEvent,
    NumberCellEditor
} from 'ag-grid-community';
import {
    isEventTargetInPrimeDialog,
    isEventTargetInPrimeOverlay,
    stopEditingAndMoveTabHorizontally
} from '../cell-renderer/prime-cell-editor-tab.util';
import {CommonHelperService} from '../../service/common-helper.service';
import {escapeHtml} from '../../utils/html.util';
import {
    catchError,
    combineLatest,
    EMPTY,
    expand,
    forkJoin,
    from,
    map,
    mergeMap,
    Observable,
    of,
    reduce,
    shareReplay,
    Subject,
    switchMap,
    take,
    takeUntil,
    throwError
} from 'rxjs';
import {isSafeWebUrl} from '../../utils/url.util';
import {
    CustomMultiSelectFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-filter.component';
import {
    CustomMultiSelectFloatingFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-floating-filter.component';
import {FieldRule, ValidationModel} from '../../model/validation.model';
import {MultiSelectDropdownCellEditorComponent} from '../cell-renderer/multi-select-dropdown-cell-editor.component';
import {ComponentIdPickerDialogComponent} from '../cell-renderer/component-id-picker-dialog.component';
import {UserSearchCellEditorComponent} from '../cell-renderer/user-search-cell-editor.component';
import {ActionsCellRendererComponent} from '../cell-renderer/actions-cell-renderer.component';
import {ImageCellRenderComponent} from '../cell-renderer/image-cell-render.component';
import {environment} from '../../../environments/environment';
import {AttachmentCellRendererComponent} from '../cell-renderer/attachment-cell-renderer.component';
import {MemberDescription, ObjectDescription} from '../../model/objectDescription';
import {buildGridPageParams, ComponentIdOption, GridPageRequest} from '../../model/gridPageRequest';
import {ConfirmEventType} from 'primeng/api';
import {
    buildConglomerationFromRow,
    getOutOfSyncFieldsForConglomeration,
    getSegmentFromConglomeration,
    parseConglomerationToRow,
    updateConglomerationSegmentOnRow
} from '../../utils/conglomeration.util';
import {ObjectDropdownCellEditorComponent} from '../cell-renderer/object-dropdown-cell-editor.component';
import {UrlCellRendererComponent} from '../cell-renderer/url-cell-renderer.component';
import {MultiImageCellRenderComponent} from '../cell-renderer/multi-image-cell-render.component';
import {
    ComponentImageType,
    getComponentImageIdsForType,
    setComponentImageIdsForType
} from '../../utils/component-image-types.util';
import {
    DropdownWithDescriptionCellRendererComponent
} from '../cell-renderer/dropdown-with-description-cell-renderer.component';
import {HoleCriteriaEditorComponent} from '../cell-renderer/hole-criteria-editor.component';
import {DynamicDialogRef} from 'primeng/dynamicdialog';
import {ViewDetailDialogComponent} from '../cell-renderer/view-detail-dialog.component';
import {CollapsibleGroupHeaderComponent} from '../CollapsibleGroupHeaderComponent';
import {AdvancedFilterModalComponent} from '../filter-components/advanced-filter-modal.component';
import {
    ADVANCED_FILTER_FIELD_BUILDERS,
    AdvancedFilterCriteria,
    AdvancedFilterFieldsBuilder
} from '../filter-components/advanced-filter-field-configs';
import {DiagTestProbeRelationshipDialogComponent} from '../cell-renderer/diag-test-probe-relationship-dialog.component';
import {
    TerminalReleaseToolRelationshipDialogComponent
} from '../cell-renderer/terminal-release-tool-relationship-dialog.component';
import {
    DevelopmentStatusReasonDialogComponent
} from '../development-status-reason-dialog/development-status-reason-dialog.component';

interface RowWriteRuleMetadata {
    writeControlField: string;
    writeAllowedForAllFieldsWhenValueIn: string[];
}

@Component({
    selector: 'app-esci-component-grid',
    templateUrl: './esci-component-grid.component.html',
    styleUrls: ['./esci-component-grid.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class EsciComponentGridComponent extends EditableGridComponent implements OnInit, OnDestroy {
    private static readonly RESTRICTED_INT = -2146000000;
    private static readonly APPROVED_DEVELOPMENT_STATUS = 'approved';
    private static readonly EDITABLE_DEVELOPMENT_STATUSES = ['draft', 'pending'];
    /** Long enough to coalesce the events one filter interaction produces, short enough to feel immediate. */
    private static readonly SERVER_FILTER_DEBOUNCE_MS = 300;
    /** How many options one picker search asks for; the server refuses a request for more than its own cap. */
    private static readonly ID_OPTION_SEARCH_LIMIT = 50;
    /** What the server answers a save with when the row repeats one already in the table. */
    private static readonly DUPLICATE_REFUSED_STATUS = 409;
    private static readonly SAVE_SETTLE_POLL_MS = 200;
    private static readonly MAX_SAVE_SETTLE_ATTEMPTS = 150;
    /**
     * Set when saveAll() (or the development-status-reason prompt it can open) is known not to have started a save,
     * so runAfterSaveSettles() does not spend its whole MAX_SAVE_SETTLE_ATTEMPTS x SAVE_SETTLE_POLL_MS (30s) window
     * silently polling `saving`/`dataChanged` for a save that will never happen. Invalid rows are known
     * synchronously (saveAll() returns without ever setting `saving`); a cancelled reason dialog is known as soon
     * as its onCancel fires - both leave `dataChanged` true and `saving` false forever, which those two flags alone
     * cannot tell apart from "still saving".
     */
    private saveAttemptAbandoned = false;
    /** Page size loadFullComponentList() fetches at a time, matching the AG Grid paginationPageSize convention used
     * by other large-catalog grids in this codebase (see aggregator-io-matrix, io-matrix-workflow). */
    private static readonly FULL_LIST_FETCH_CHUNK_SIZE = 1000;
    // Seed from snapshot so initial template pass does not see null/undefined before ngOnInit subscription runs.
    componentName: string = this.route?.snapshot?.params?.['componentName'] || '';
    showAdvancedFilter = false;
    objectDescription: ObjectDescription | null = null;
    private fieldRules: FieldRule[] = [];
    saveBaseUrl = '/esciUI/saveObject/';
    ref: DynamicDialogRef;
    fieldForDeleteMessage = 'id';

    permissionBaseUrl      = '/esciUI/viewComponentPermission/';
    adminPermissionBaseUrl = '/esciUI/adminComponentPermission/';
    permissionUrl      = this.permissionBaseUrl;      // These will get updated once we know our component name
    adminPermissionUrl = this.adminPermissionBaseUrl; // These will get updated once we know our component name
    components: [];
    adminOnlyColumns = ['Actions'];
    cols: (ColDef | ColGroupDef)[] = [];
    private componentImageTypes: ComponentImageType[] = [];
    activeFiltersMap: Map<string, string[]> = new Map();
    activeFiltersArray: { key: string, value: string[] }[] = [];
    /** URL query params from initial load; used to pass to getObjects and show Active Search Criteria */
    initialQueryParams: { [key: string]: string | string[] } = {};
    private readonly defaultGridFilters = signal<Record<string, string[]>>({});
    private readonly editPermissionsResolved = signal(false);
    readonly isReadOnlyUser = computed(() => this.editPermissionsResolved() &&
        !this.adminPermission() && !this.hasPurchAdminPermission() && !this.hasTierNAdminPermission());
    private readOnlyAdvancedFilterOpened = false;
    private readonly destroyed = signal(false);
    ADVANCED_FILTER = 'Advanced Filter';
    inSetColumnVisibilityAndOrder = false;
    private columnMoveTimer: any;
    /**
     * When false, column move/resize must not schedule persist (avoids saving before server user data
     * is applied to the grid after load/navigation).
     */
    private columnPersistReady = false;
    private componentColumnsReady = false;
    private componentDataGeneration = 0;
    private cachedFlatCols: ColDef[] | null = null; // Cache for flattened column definitions to improve performance
    private readonly pendingColumnDefs =
        signal<{ loadingFor: string; generation: number; newCols: (ColDef | ColGroupDef)[] } | null>(null);
    private adminVisibilityEffectRef: EffectRef | null = null;
    @ViewChild('agGrid', {read: ElementRef}) private agGridHost?: ElementRef<HTMLElement>;
    private gridTabCaptureListener: ((ev: Event) => void) | null = null;

    // PPCO support for PURCH_ADMIN-only users
    hasPPCO = false;
    ppcoDataChanged = false;

    // True if user has PURCH_ADMIN but not component admin permission
    get isPurchAdminOnly(): boolean {
        return this.hasPurchAdminPermission() && !this.adminPermission();
    }

    /** Component supports a Tier-N Resilient FK; populated from common-helper at init. */
    hasTierNResilient = false;
    /** Becomes true when at least one row has a pending tierNResilient.* change. */
    tierNDataChanged = false;

    /** True iff the user has COMPONENT_TIER_N_ADMIN but no full component edit permission. */
    get isTierNAdminOnly(): boolean {
        return this.hasTierNAdminPermission() && !this.adminPermission();
    }

    /** Default page size; must be one of {@link paginationPageSizeSelector} for the panel's picker to mark it. */
    paginationPageSize = 20;
    paginationPageSizeSelector: number[] = [10, 20, 50, 100, 200];

    /**
     * Rows per page. Every grid pages, sorts and filters on the server, so this is what the paginator asks for and
     * nothing else decides it; the user changes it through the paginator's own page-size selector.
     */
    readonly serverPageSize = signal(this.paginationPageSize);
    /** Zero-based index of the page currently displayed. */
    readonly serverPageIndex = signal(0);
    /** Rows matching the current filters across the whole table, as reported by the server. */
    readonly serverTotalRows = signal(0);
    /**
     * Whether this catalog is opted into server-side paging (ObjectDescription.serverPaged), set fresh from the
     * description on every load. False (the default until a catalog opts in) falls back to AG Grid's own
     * client-side pagination/filter/sort over the whole list - see loadFullComponentList().
     */
    readonly serverPaged = signal(false);
    /** The whole list for this catalog in client (opt-out) mode, used only to restore Advanced Filter's "clear". */
    private unfilteredRowData: ValidationModel[] = [];
    /**
     * What the paging panel reports, in the terms AG Grid's own panel uses. AG Grid cannot work these out here: it
     * counts the rows it holds, and it holds one page, so they come from the count the server sent instead.
     */
    readonly firstRowOnPage = computed(() => (this.serverTotalRows() === 0)
                                            ? 0 : this.serverPageIndex() * this.serverPageSize() + 1);
    readonly lastRowOnPage = computed(() => Math.min((this.serverPageIndex() + 1) * this.serverPageSize(),
                                                    this.serverTotalRows()));
    /** One page even with nothing on it, so the panel reads "Page 1 of 1" rather than "of 0". */
    readonly totalServerPages = computed(() => Math.max(1, Math.ceil(this.serverTotalRows() /
                                                                     Math.max(this.serverPageSize(), 1))));
    readonly currentServerPage = computed(() => this.serverPageIndex() + 1);
    readonly onFirstServerPage = computed(() => this.serverPageIndex() <= 0);
    readonly onLastServerPage = computed(() => this.serverPageIndex() >= this.totalServerPages() - 1);
    /** Whether the page-size list is showing. The picker is the panel's own, so the panel holds its state. */
    readonly pageSizePickerOpen = signal(false);
    /** Which size the open list has marked, so the arrow keys can move through it before one is chosen. */
    readonly pageSizePickerHighlight = signal(0);
    private serverSortBy: string | null = null;
    private serverSortDir: 'asc' | 'desc' = 'asc';
    /** Set while this component itself writes the grid's filter or sort state, so that does not read as a user change. */
    private applyingServerGridState = false;
    /**
     * Whether a mutation the server accepted has left the page owing a re-read. Set when a create or delete lands
     * while other rows on the page are still unsaved, since re-reading replaces rowData and would take those edits
     * with it; paid by the first save after that which leaves the page clean.
     */
    private serverPageNeedsReconcile = false;
    /** The request that produced the rows on screen; a repeat of it is not worth another round trip. */
    private lastServerRequestSignature = '';
    /**
     * Counts page requests issued, so an answer can be matched to the request that asked for it. Neither the
     * catalog name, the load generation nor the signature can do that: a forced read skips the signature check by
     * design, so two requests identical in all three can be in flight at once.
     */
    private serverPageRequestGeneration = 0;
    /** Set while a page is being read and the rows on screen are covered, so nothing else claims the overlay. */
    private serverPageRequestInFlight = false;
    /** How many referenced ids one lookup request carries; a page of rows can name far more than this between them. */
    private static readonly OFF_PAGE_ID_CHUNK_SIZE = 200;
    /** How many of an export's id-lookup chunks are asked for at a time, so hundreds cannot be in flight at once. */
    private static readonly EXPORT_LOOKUP_CONCURRENCY = 4;
    private serverFilterDebounceTimer: any;
    /** Grid filter and sort state that the displayed page matches, restored when a user cancels a change. */
    private lastAppliedFilterModel: any = null;
    private lastAppliedSortState: { colId: string, sort: 'asc' | 'desc' | null }[] = [];
    /** What the Advanced Filter dialog last asked for, applied alongside the grid's own column filters. */
    private advancedFilterCriteria: AdvancedFilterCriteria | null = null;
    /** Options the picker's searches have turned up, keyed by id, for values the grid's page cannot supply. */
    private readonly componentIdOptions = new Map<number, ComponentIdOption>();
    /** Rows named by an id on the page but not on it themselves, so their display columns still have values. */
    private readonly offPageRowsById = new Map<number, any>();
    private readonly offPageIdsRequested = new Set<number>();
    /** Complete server result set used only while derived grouped columns resolve values for an export. */
    private exportRowsById: Map<number, ValidationModel> | null = null;
    private exportGeneration = 0;
    private readonly exportCancellation = new Subject<void>();
    private static readonly EXPORT_PAGE_SIZE = 1000;
    private static readonly MAX_CLIENT_EXPORT_ROWS = 10000;

    /**
     * Inserts a blank new row (only id is -1) and opens the same create dialog as Copy.
     */
    onAddNewClick(): void {
        if (!this.adminPermission()) {
            return;
        }
        const newRow = this.buildBlankNewRow();
        this.rowData.unshift(newRow);
        this.refreshGrid(this.rowData);
        this.onViewDetailsClick({ data: newRow }, true);
    }

    /**
     * Builds a new row with id -1, state 'new', grid metadata, and column fields cleared to null
     * (or [] for collection paths that use [] in the field name).
     */
    private buildBlankNewRow(): ValidationModel {
        const row: any = {
            id: -1,
            state: 'new',
            changes: new Map(),
            fieldRules: this.fieldRules,
        };
        const flatCols = this.flattenColDefs(this.cols);
        const fieldNames = new Set<string>(
            flatCols
                .map((col) => col.field as string | undefined)
                .filter((field): field is string => !!field)
        );
        const arrayPathsDone = new Set<string>();

        for (const col of flatCols) {
            const field = col.field as string | undefined;
            if (!field) {
                continue;
            }
            if (col.colId === 'Actions' || field === 'Actions') {
                continue;
            }
            if (typeof col.colId === 'string' && col.colId.includes('__placeholder')) {
                continue;
            }
            if (field === 'id') {
                continue;
            }

            if (field.includes('[]')) {
                const [pathToArray] = field.split('[]');
                if (!arrayPathsDone.has(pathToArray)) {
                    arrayPathsDone.add(pathToArray);
                    CommonHelperService.setDeepValue(row, pathToArray, []);
                }
                continue;
            }

            CommonHelperService.setDeepValue(row, field, null);
        }

        this.applyConglomerationDefaultsForNewRow(row, fieldNames);
        return row as ValidationModel;
    }

    /**
     * New rows initialize all fields to null. For conglomeration fields, rebuild a visible template
     * (including separators) and infer hidden constant segments from existing rows when stable.
     */
    private applyConglomerationDefaultsForNewRow(row: any, fieldNames: Set<string>): void {
        const members = this.objectDescription?.members || [];
        const conglomerationMembers = members.filter((member) =>
            member.type === 'conglomeration' && !!member.fields && !!member.memberName
        );
        for (const member of conglomerationMembers) {
            const conglomerationFieldName = member.memberName;
            const conglomerationFields = member.fields as string;
            const tokens = conglomerationFields.split(',').map((t: string) => t.trim()).filter((t: string) => !!t);
            for (const token of tokens) {
                if (token === '-' || token === conglomerationFieldName || fieldNames.has(token)) {
                    continue;
                }
                const inferredValue = this.inferStableConglomerationTokenValue(conglomerationFieldName,
                                                                               conglomerationFields,
                                                                               token);
                if (inferredValue) {
                    CommonHelperService.setDeepValue(row, token, inferredValue);
                }
            }
            CommonHelperService.setDeepValue(row,
                                             conglomerationFieldName,
                                             buildConglomerationFromRow(row, conglomerationFields));
        }
    }

    /**
     * Infer a token value only when all existing rows resolve to one distinct non-empty segment.
     * This is used for hidden constant conglomeration segments (for example protectionCommodityCode).
     */
    private inferStableConglomerationTokenValue(
        conglomerationFieldName: string,
        conglomerationFields: string,
        token: string
    ): string {
        const existingRows = this.rowData || [];
        if (existingRows.length === 0) {
            return '';
        }
        const distinctValues = new Set<string>();
        for (const existingRow of existingRows) {
            const directValue = CommonHelperService.getDeepValue(existingRow, token);
            const directString = directValue == null ? '' : String(directValue).trim();
            if (directString) {
                distinctValues.add(directString);
            } else {
                const conglomerationValue = CommonHelperService.getDeepValue(existingRow, conglomerationFieldName);
                const conglomerationString = conglomerationValue == null ? '' : String(conglomerationValue).trim();
                if (conglomerationString) {
                    const segment = getSegmentFromConglomeration(conglomerationString, conglomerationFields, token).trim();
                    if (segment) {
                        distinctValues.add(segment);
                    }
                }
            }
            if (distinctValues.size > 1) {
                return '';
            }
        }
        return distinctValues.size === 1 ? Array.from(distinctValues)[0] : '';
    }


    override afterPrimaryGridReady(_params: GridReadyEvent): void {
        // Normalize admin-only column visibility once the primary grid API is live.
        this.setColumnsVisible();
        // A page asked for before the grid existed raised the in-flight flag with nothing to show it on. The flag
        // then tells onFilterChanged the rows are covered, so it leaves the overlay alone too - and the grid sits
        // blank and idle-looking until the answer lands. This is the first moment there is something to cover.
        if (this.serverPageRequestInFlight) {
            this.showServerRefetchOverlay();
        }
    }

    ngOnInit(): void {
        this.adminVisibilityEffectRef = effect(() => {
            this.adminPermission();
            this.setColumnsVisible();
        }, {injector: this.injector});

        this.gridOptions.onCellKeyDown = (e) => {
            if ('colDef' in e && e.colDef) {
                this.handleGridLinkCellKeyDown(e as CellKeyDownEvent);
            }
        };
        this.cellClassRules['conglomeration-out-of-sync'] = (params) =>
            !!params.data?._conglomerationOutOfSyncFields?.has(params.colDef?.field);
        this.rowClassRules['approved-locked-row'] = (params) => this.isApprovedLockedRow(params?.data);
        this.fetchPersistentUserData();
        combineLatest([this.route.params, this.route.queryParams]).subscribe(([params, queryParams]) => {
            // Defer route-driven state changes to the next microtask so Angular does not observe
            // value transitions within the same initial check cycle (NG0100 in dev mode).
            Promise.resolve().then(() => this.applyComponentSwitch(params['componentName'], queryParams));
        });
    }

    /**
     * Applies all per-component-switch state resets and kicks off loading the newly selected
     * componentName's data. Runs once per route param change (see ngOnInit's route.params
     * subscription), including on initial navigation to a component grid.
     */
    private applyComponentSwitch(componentName: string, queryParams: any): void {
        // Clear row data immediately so we never show the previous grid's data with the new
        // grid's column structure, and prevent opening details on a stale row (wrong type).
        this.setRowData([]);
        this.loading = true;
        this.columnPersistReady = false;
        // A component switch invalidates the previous component's Active Search Criteria
        // (including any Advanced Filter), since updateActiveFilters() otherwise re-preserves
        // a stale Advanced Filter entry from activeFiltersMap indefinitely across grids.
        this.activeFiltersMap = new Map();
        this.activeFiltersArray = [];
        if (this.columnMoveTimer) {
            clearTimeout(this.columnMoveTimer);
            this.columnMoveTimer = null;
        }
        // A refresh still pending for the grid being left would ask for the new grid's rows under the old filters.
        if (this.serverFilterDebounceTimer) {
            clearTimeout(this.serverFilterDebounceTimer);
            this.serverFilterDebounceTimer = null;
        }
        // A request already sent for the grid being left will be recognised as stale and dropped, so it never
        // lowers the in-flight flag itself. Left raised, it tells the catalog being switched to that its rows are
        // covered, and every onFilterChanged there skips the base filter pass and the no-rows overlay for good.
        this.hideServerRefetchOverlay();
        // The ids a row may point at, and the rows read for ids off the page, belong to the component they came from.
        // They are keyed by bare numbers, so anything kept here would render the previous component's values beside a
        // matching id of the new one.
        this.componentIdOptions.clear();
        this.offPageRowsById.clear();
        this.offPageIdsRequested.clear();
        this.exportCancellation.next();
        this.exportGeneration++;
        this.exportRowsById = null;
        this.exporting = false;
        // Default to client mode (the safer, simpler state) until the new description names this catalog's own
        // mode; otherwise the previous catalog's panel/pagination could flash for the tick before it arrives.
        this.serverPaged.set(false);
        this.unfilteredRowData = [];
        this.componentName = componentName;
        this.objectDescription = null;
        this.componentColumnsReady = false;
        // Set overlay message before grid shows/refreshes to see "Loading <name> data...", not "No matching data..."
        this.gridOptions.noRowsOverlayComponentParams = this.getNoRowsOverlayParams();
        if (this.gridApi) {
            this.gridApi.hideOverlay();
            this.refreshGrid(this.rowData, null, () => this.gridApi?.showNoRowsOverlay());
        }
        this.initialQueryParams = {...queryParams};
        this.editPermissionsResolved.set(false);
        this.readOnlyAdvancedFilterOpened = false;
        this.showAdvancedFilter = !!this.getAdvancedFilterFieldsBuilder(this.componentName);
        // hasPPCO / hasTierNResilient are derived from the column set inside replaceCols()
        // once the backend ObjectDescription has been resolved, rather than from a hardcoded
        // component-name list — see CommonHelperService.hasFieldInColumns.
        this.loadComponentData();
        this.saveUrl = this.saveBaseUrl + this.componentName;
        this.messageService.clear();
        this.permissionUrl = this.permissionBaseUrl + this.componentName;
        this.adminPermissionUrl = this.adminPermissionBaseUrl + this.componentName;
        this.esciService.getPermissionAndFileSizeLimit(this, false);
        this.esciService.checkExportPermission(this);
    }

    ngOnDestroy(): void {
        this.destroyed.set(true);
        this.componentDataGeneration++;
        this.exportCancellation.next();
        this.exportCancellation.complete();
        this.exportGeneration++;
        this.exportRowsById = null;
        this.exporting = false;
        this.pendingColumnDefs.set(null);
        if (this.columnMoveTimer) {
            clearTimeout(this.columnMoveTimer);
            this.columnMoveTimer = null;
        }
        // A pending filter refresh would otherwise fire against a grid that no longer exists.
        if (this.serverFilterDebounceTimer) {
            clearTimeout(this.serverFilterDebounceTimer);
            this.serverFilterDebounceTimer = null;
        }
        if (this.adminVisibilityEffectRef) {
            this.adminVisibilityEffectRef.destroy();
            this.adminVisibilityEffectRef = null;
        }
        this.detachGridEditingTabCapture();
    }

    onAccessPermissionsResolved(): void {
        if (this.destroyed()) {
            return;
        }
        this.editPermissionsResolved.set(true);
        if (this.serverPaged()) {
            this.getData();
        } else if (this.isReadOnlyUser() && this.objectDescription?.members) {
            this.applyingServerGridState = true;
            try {
                this.applyInitialQueryParamsToActiveFiltersAndGrid();
            } finally {
                this.applyingServerGridState = false;
            }
        }
        this.tryOpenReadOnlyAdvancedFilter();
    }

    private tryOpenReadOnlyAdvancedFilter(): void {
        if (!this.permission() || !this.isReadOnlyUser() || !this.showAdvancedFilter || !this.objectDescription?.members ||
            this.readOnlyAdvancedFilterOpened) {
            return;
        }
        this.readOnlyAdvancedFilterOpened = true;
        this.advancedFilter();
    }

    onCustomButtonClick(customButtonURL: string): void {
        if (customButtonURL) {
            window.open(customButtonURL, '_blank');
        }
    }
    onAttachmentChanged(): void {
        this.refreshGrid(this.rowData);
    }

    protected getDynamicNoRowsMessage(): string {
        // The intent of this method is to give custom message depending on the component type,
        // however, it's called before the component is set up, returning 'No matching data. Try adjusting your filters.'
        // When it's called later to update it, the message it returns isn't properly being updated on the UI. :(
        if (this.componentName === 'RingTerminal') {
            return `No matching Ring Terminals. Try adjusting your filters or clicking the 'More Information' button.`;
        }
        const objName = this.objectDescription?.displayName || this.componentName || '';
        return `No matching ${objName} data. Try adjusting your filters.`;
    }

    getGridDisplayName(): string {
        if (this.objectDescription?.displayName) {
            return this.objectDescription.displayName;
        }
        return this.humanizeComponentName(this.componentName);
    }

    private humanizeComponentName(name: string): string {
        if (!name) {
            return '';
        }
        return name.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
    }

    /**
     * Message shown in the grid overlay when there are no rows. Shows "Loading &lt;name&gt;..." while
     * data is loading, otherwise the normal no-rows message.
     */
    protected getNoRowsOverlayMessage(): string {
        if (this.loading && (!this.rowData || this.rowData.length === 0)) {
            const name = this.objectDescription?.displayName || this.componentName || 'data';
            return `Loading ${name} data...`;
        }
        return this.getDynamicNoRowsMessage();
    }

    /** Params for the no-rows overlay (message and getters for loading state and text). */
    protected getNoRowsOverlayParams(): { message: string; messageGetter: () => string; isLoadingGetter: () => boolean } {
        return {
            message: this.getNoRowsOverlayMessage(),
            messageGetter: () => this.getNoRowsOverlayMessage(),
            isLoadingGetter: () => this.loading && (!this.rowData || this.rowData.length === 0)
        };
    }

    /**
     * Params for the overlay covering the grid while a page is being re-read. It reuses the no-rows overlay component
     * rather than `overlayLoadingTemplate`, which AG Grid injects as raw HTML - the catalog name in the message comes
     * from a route parameter, so it must reach the DOM through interpolation and not markup.
     */
    private getServerRefetchOverlayParams(): { message: string; messageGetter: () => string;
                                               isLoadingGetter: () => boolean } {
        const loadingMessage = () => `Loading ${this.objectDescription?.displayName || this.componentName || 'data'} ` +
                                     `data...`;
        return {
            message: loadingMessage(),
            messageGetter: loadingMessage,
            isLoadingGetter: () => true
        };
    }

    /**
     * Covers the rows while the page they are being replaced by is read. Sorting or filtering a large table takes
     * long enough to read the grid, and rows left visible under a sort indicator or filter chip they do not match
     * state something untrue about the data.
     */
    private showServerRefetchOverlay(): void {
        this.serverPageRequestInFlight = true;
        if (!this.isGridApiAlive(this.gridApi)) {
            return;
        }
        this.gridOptions.loadingOverlayComponent = 'customNoRowsOverlay';
        this.gridOptions.loadingOverlayComponentParams = this.getServerRefetchOverlayParams();
        this.gridApi.showLoadingOverlay();
    }

    /** Uncovers the rows once the page has settled, whether the request answered or failed. */
    private hideServerRefetchOverlay(): void {
        this.serverPageRequestInFlight = false;
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.hideOverlay();
        }
    }

    override cellEditingStopped(event: CellEditingStoppedEvent<any>, rows: ValidationModel[]): void {
        const field = event.column?.getColDef()?.field;
        if (field && this.objectDescription?.members) {
            const member = this.objectDescription.members.find((m: MemberDescription) => m.memberName === field);
            if (member?.parentConglomeration?.fields) {
                const parent = member.parentConglomeration;
                const row = event.data;
                // Prefer the value from the stop event so Tab-out matches the editor output (row[field] can lag).
                const editedValue = event.newValue !== undefined ? event.newValue : row[field];
                updateConglomerationSegmentOnRow(row, parent.memberName, parent.fields, field, editedValue);
                this.recomputeConglomerationOutOfSyncForRow(row, parent.memberName, parent.fields);
                // Row/cell refresh is handled in super (deferred redraw avoids killing same-row Tab target editor).
            }
        }
        super.cellEditingStopped(event, rows);
        // Keep the PPCO/Tier-N "Save"/"Revert" button enable state in sync immediately after a cell edit.
        // super.cellEditingStopped only refreshes dataChanged + row.changes; the per-feature flags those
        // buttons bind to ([disabled]="!ppcoDataChanged" / "!tierNDataChanged") are otherwise only updated
        // on save/revert/refreshGrid, leaving PURCH_ADMIN-only or COMPONENT_TIER_N_ADMIN-only users with
        // disabled buttons after editing a cell.
        if (this.hasPPCO) {
            this.updatePPCODataChanged();
        }
        if (this.hasTierNResilient) {
            this.updateTierNDataChanged();
        }
    }

    protected override validateOnlyEditedCellOnCellEdit(): boolean {
        return true;
    }

    /**
     * Do not use the parent's delayed sizeColumnsToFit() — it fires ~200ms after grid ready, which is
     * after applySavedColumnWidthsToGrid (inside the reset callback ~100ms) and wipes persisted widths.
     * Also skip fitCellContents auto-size (see template): it resizes columns when row data loads.
     */
    override onGridReady(params: GridReadyEvent, key?: string): void {
        if (key) {
            super.onGridReady(params, key);
            return;
        }
        super.onGridReady(params);
        if (this.cols?.length) {
            this.gridApi.setGridOption('columnDefs', this.cols);
        }
        // After next render so #agGrid ViewChild exists once *ngIf has created the grid host.
        afterNextRender(() => this.attachGridEditingTabCapture(), {injector: this.injector});
    }

    /**
     * Capture Tab before the browser moves focus (native inputs / portaled PrimeNG overlays).
     * Commit edit and advance one displayed column, same order as Tab when not editing.
     */
    private attachGridEditingTabCapture(): void {
        this.detachGridEditingTabCapture();
        this.gridTabCaptureListener = (e: Event) => {
            if (!this.gridApi || !(e instanceof KeyboardEvent) || e.key !== 'Tab') {
                return;
            }
            if (this.gridApi.getEditingCells().length === 0) {
                return;
            }
            const host = this.agGridHost?.nativeElement;
            const t = e.target as Node | null;
            if (!t) {
                return;
            }
            // Modal cell editors (Hole Criteria, etc.) handle Tab inside p-dialog; do not stopEditing().
            if (isEventTargetInPrimeDialog(t)) {
                return;
            }
            const insideGrid = !!host?.contains(t);
            const insidePrimeOverlay = isEventTargetInPrimeOverlay(t);
            if (!insideGrid && !insidePrimeOverlay) {
                return;
            }
            e.preventDefault();
            e.stopImmediatePropagation();
            stopEditingAndMoveTabHorizontally(this.gridApi, e.shiftKey, e);
        };
        document.addEventListener('keydown', this.gridTabCaptureListener, true);
    }

    private detachGridEditingTabCapture(): void {
        if (this.gridTabCaptureListener) {
            document.removeEventListener('keydown', this.gridTabCaptureListener, true);
            this.gridTabCaptureListener = null;
        }
    }

    override onFilterChanged(): void {
        this.restoreReadOnlyDefaultFilters();
        this.gridOptions.noRowsOverlayComponentParams = this.getNoRowsOverlayParams();
        if (this.serverPageRequestInFlight) {
            // The rows are covered while their replacement is read. Both the base class and the branch below would
            // otherwise swap that overlay for "No matching data", purely because the grid is displaying nothing yet,
            // and a busy grid would read as an empty one. Only the filter hook is still owed.
            this.updateActiveFilters();
        } else {
            super.onFilterChanged();
            if (this.gridApi && this.gridApi.getDisplayedRowCount() === 0) {
                this.gridApi.hideOverlay();
                this.gridApi.showNoRowsOverlay();
            }
        }
        if (this.serverPaged() && !this.applyingServerGridState) {
            this.scheduleServerPageRefresh();
        }
    }

    private restoreReadOnlyDefaultFilters(): void {
        if (!this.isGridApiAlive(this.gridApi)) {
            return;
        }
        const lockedDefaults = this.readOnlyDefaultFiltersForPage();
        if (Object.keys(lockedDefaults).length === 0) {
            return;
        }
        const filterModel = this.gridApi.getFilterModel() || {};
        const restoredModel = {...filterModel};
        let changed = false;
        for (const [memberName, values] of Object.entries(lockedDefaults)) {
            const currentValues = EsciComponentGridComponent.toFilterValues(
                filterModel[memberName]?.values ?? filterModel[memberName]
            );
            if (currentValues.length === values.length &&
                currentValues.every((value, index) => value === values[index])) {
                continue;
            }
            restoredModel[memberName] = EsciComponentGridComponent.buildFilterModelEntry(values);
            changed = true;
        }
        if (changed) {
            this.gridApi.setFilterModel(restoredModel);
        }
    }

    onColumnMoved(event: any): void {
        this.schedulePersistColumnData();
    }

    onColumnResized(): void {
        this.schedulePersistColumnData();
    }

    /**
     * Debounce: wait for a few seconds after the last column move or resize before persisting
     * column order and widths to user data.
     */
    private schedulePersistColumnData(): void {
        if (!this.columnPersistReady || !this.persistentUserData) {
            return;
        }
        if (this.columnMoveTimer) {
            clearTimeout(this.columnMoveTimer);
        }
        this.columnMoveTimer = setTimeout(() => {
            this.persistColumnData();
            this.columnMoveTimer = null;
        }, 5_000);
    }

    /**
     * Set child.parentConglomeration and member.conglomerationChildren for each conglomeration member.
     * Must run before convertMemberToColumn() so cellEditorParams.parentConglomeration is populated for constituents.
     * Idempotent: clears prior parent/child links so repeated calls (e.g. column rebuild on the same ObjectDescription)
     * do not accumulate duplicate children or leave stale parentConglomeration pointers.
     */
    private linkConglomerationParentRefs(objDesc: ObjectDescription): void {
        const members = objDesc.members || [];
        for (const m of members) {
            m.parentConglomeration = undefined;
        }
        const conglomerationMembers = members.filter(m => m.type === 'conglomeration');
        for (const member of conglomerationMembers) {
            member.conglomerationChildren = [];
        }

        const byMemberName = new Map<string, MemberDescription[]>();
        for (const m of members) {
            const name = m.memberName?.trim();
            if (!name) {
                continue;
            }
            let bucket = byMemberName.get(name);
            if (!bucket) {
                bucket = [];
                byMemberName.set(name, bucket);
            }
            bucket.push(m);
        }

        for (const member of conglomerationMembers) {
            const fields = member.fields?.split(',') || [];
            for (const field of fields) {
                const trimmed = field.trim();
                if (trimmed === '-') {
                    continue;
                }
                const childSet = byMemberName.get(trimmed);
                if (childSet?.length === 1) {
                    const child = childSet[0];
                    member.conglomerationChildren.push(child);
                    child.parentConglomeration = member;
                }
            }
        }
    }

    private findRowWriteRuleMetadata(objDesc: ObjectDescription): RowWriteRuleMetadata | null {
        const members = objDesc?.members || [];
        for (const member of members) {
            const writeControlField = String(member?.writeControlField || '').trim();
            if (!writeControlField) {
                continue;
            }
            const allowedForAllFields = (member?.writeAllowedForAllFieldsWhenValueIn || [])
                .map(v => String(v || '').trim())
                .filter(v => v.length > 0);
            if (allowedForAllFields.length === 0) {
                continue;
            }
            return {
                writeControlField,
                writeAllowedForAllFieldsWhenValueIn: allowedForAllFields
            };
        }
        return null;
    }

    private convertObjectDescriptionToColumns(
        objDesc: ObjectDescription,
        path: String[] = [],
        inheritedRowWriteRule: RowWriteRuleMetadata | null = null
    ): (ColDef | ColGroupDef)[] {
        const newCols = [];
        // Only reset field rules on the root describe call. Nested object descriptions (children) recurse
        // into this method; clearing here would drop root-level rules (e.g. uniqueFieldSetName on gmPartNumber)
        // and leave rows with fieldRules that only match the last processed child subtree.
        if (path.length === 0) {
            this.fieldRules.length = 0;
        }
        const activeRowWriteRule = inheritedRowWriteRule || this.findRowWriteRuleMetadata(objDesc);

        this.linkConglomerationParentRefs(objDesc);

        for (const member of objDesc.members) {
            const col = this.convertMemberToColumn(objDesc, member, activeRowWriteRule);
            if (col) {
                newCols.push(col);
            }

            const fieldRule = this.convertObjectDescriptorIntoFieldRule(member);
            if (fieldRule) {
                this.fieldRules.push(fieldRule);
            }
        }

        // At root level, group conglomeration column and its sub-fields into "Specification Codes" column group
        if (path.length === 0) {
            this.groupConglomerationColumns(newCols);
        }

        const newPath = [...path, 'obj-' + objDesc.className];
        for (const child of objDesc.children || []) {
            const childKey = child.key;
            const childDesc = child.value;
            const displayKey = childDesc.displayName === 'ppco' ? 'PPCO' : childDesc.displayName;
            const childCols = this.convertObjectDescriptionToColumns(childDesc, newPath, activeRowWriteRule);
            const colClass = [...newPath, 'obj-' + childDesc.className].join(' ');

            const groupedCols = childCols.filter(col =>
                // Don't display the child id field in its own column.
                !(typeof col === 'object' && 'field' in col && col.field?.endsWith('.id'))
            );
            childCols.forEach(col => {
                if (!('children' in col) && 'field' in col) {
                    let cellClass = (col as ColDef).cellClass;
                    if (Array.isArray(cellClass)) {
                        cellClass = [...cellClass, colClass];
                    } else if (typeof cellClass === 'string') {
                        cellClass = [cellClass, colClass];
                    } else {
                        cellClass = colClass; // no cellClass defined, so we set it to the colClass
                    }
                    (col as ColDef).cellClass = cellClass;
                    (col as ColDef).headerClass = colClass;

                    // Add valueGetter and valueSetter for nested fields to properly handle nested objects
                    const colDef = col as ColDef;
                    if (colDef.field && colDef.field.includes('.')) {
                        colDef.valueGetter = (params) => {
                            if (!params.data) return undefined;
                            return CommonHelperService.getDeepValue(params.data, colDef.field);
                        };
                        colDef.valueSetter = (params) => {
                            if (!params.data) return false;
                            CommonHelperService.setDeepValue(params.data, colDef.field, params.newValue);
                            return true;
                        };
                    }
                }
            });

            const singleIdPickerGroup = groupedCols.length === 1 &&
                                        'children' in groupedCols[0] &&
                                        String((groupedCols[0] as ColGroupDef).groupId || '').endsWith('__id_dropdown');
            const preserveSingleColumnGroup = childDesc.isCollection || singleIdPickerGroup;
            if (groupedCols.length === 1 && !preserveSingleColumnGroup) {
                // There is only one column in this group, so we can just add it directly
                const singleCol = groupedCols[0] as ColDef;
                switch (childKey) {
                    case 'wireSizeMin': singleCol.headerName = 'Minimum ' + singleCol.headerName; break;
                    case 'wireSizeMax': singleCol.headerName = 'Maximum ' + singleCol.headerName; break;
                    case 'cavitySealPlug': singleCol.headerName = singleCol.headerName; break;
                    default: singleCol.headerName = displayKey + ' ' + singleCol.headerName;
                }
                newCols.push(singleCol);
            } else if (groupedCols.length > 0) {
                // when building each childGroup we also add a placeholder column
                const placeholderColId = `${childKey}__placeholder`;
                const placeholderCol: ColDef = {
                    colId: placeholderColId,
                    headerName: '...',
                    editable: false,
                    field: placeholderColId,         // not bound to data, it renders a static '...'
                    width: 60,
                    sortable: false,
                    lockVisible: true,
                    suppressNavigable: true,
                    hide: true,                      // hide the placeholder column by default
                    cellRenderer: params => '…',     // show ellipsis in every cell
                    cellClass: 'placeholder-cell',
                    cellStyle: { textAlign: 'center' }
                };

                const childGroup: ColGroupDef = {
                    headerName: singleIdPickerGroup ? this.humanizeFieldName(childKey) : displayKey,
                    groupId: childKey,
                    headerGroupComponent: CollapsibleGroupHeaderComponent,
                    marryChildren: true,
                    headerClass: colClass,
                    children: [ placeholderCol, ...groupedCols ]
                };
                if (childDesc.isCollection || singleIdPickerGroup) {
                    // Keep one-column collections wrapped. Flattening their subrow column into a singular parent
                    // makes the details view mistake that parent object for a child grid.
                    (childGroup as any).esciChildGrid = true;
                }
                newCols.push(childGroup);
            }
        }
        return newCols;
    }

    /**
     * Find conglomeration column(s) in newCols and group each with its sub-field columns
     * under a "Specification Codes" column group. Sub-columns are in conglomerationFields order
     * (conglomeration field first) and are not reorderable within the group.
     */
    private groupConglomerationColumns(newCols: (ColDef | ColGroupDef)[]): void {
        let conglomerationIndex = newCols.findIndex(c =>
            'field' in c && (c as ColDef).cellEditorParams?.memberType === 'conglomeration'
        );
        while (conglomerationIndex >= 0) {
            const conglomerationCol = newCols[conglomerationIndex] as ColDef;
            const conglomerationFields = (conglomerationCol.cellEditorParams?.conglomerationFields as string) || '';
            const fieldOrder = conglomerationFields.split(',')
                                                   .map((t: string) => t.trim())
                                                   .filter((t: string) => t !== '-');

            const specCols: ColDef[] = [conglomerationCol];
            for (const fieldName of fieldOrder) {
                if (fieldName === conglomerationCol.field) {
                    continue;
                }
                const found = newCols.find(c => 'field' in c && (c as ColDef).field === fieldName ) as ColDef | undefined;
                if (found) {
                    specCols.push(found);
                }
            }

            specCols.forEach(c => {
                c.suppressMovable = true;
            });

            const group: ColGroupDef = {
                headerName: 'Specification Codes',
                marryChildren: true,
                children: specCols
            };

            const indicesToRemove = specCols.map(c => newCols.indexOf(c))
                                            .filter(i => i >= 0)
                                            .sort((a, b) => b - a);
            for (const i of indicesToRemove) {
                newCols.splice(i, 1);
            }
            const insertIndex = (indicesToRemove.length > 0) ? Math.min(...indicesToRemove) : conglomerationIndex;
            newCols.splice(insertIndex, 0, group);

            conglomerationIndex = newCols.findIndex(c =>
                'field' in c && (c as ColDef).cellEditorParams?.memberType === 'conglomeration'
            );
        }
    }

    private applyRowWriteMetadataToLeafColumn(
        col: ColDef,
        member: MemberDescription,
        rowWriteRule: RowWriteRuleMetadata | null
    ): void {
        const ruleControlField = String(rowWriteRule?.writeControlField || '').trim();
        const memberControlField = String(member?.writeControlField || '').trim();
        const writeControlField = memberControlField || ruleControlField;
        const globalAllowedStates = (rowWriteRule?.writeAllowedForAllFieldsWhenValueIn || [])
            .map(v => String(v || '').trim())
            .filter(v => v.length > 0);
        const hasMetadata = !!writeControlField || globalAllowedStates.length > 0 || !!member?.writeAlwaysAllowed;
        if (!hasMetadata) {
            return;
        }
        const editorParams: any = col.cellEditorParams || {};
        editorParams.writeControlField = writeControlField;
        editorParams.writeAllowedForAllFieldsWhenValueIn = [...globalAllowedStates];
        editorParams.writeAlwaysAllowed = !!member?.writeAlwaysAllowed;
        col.cellEditorParams = editorParams;
    }

    private applyRowWriteMetadataToColumn(
        col: ColDef | ColGroupDef,
        member: MemberDescription,
        rowWriteRule: RowWriteRuleMetadata | null
    ): void {
        if ('children' in col) {
            for (const child of col.children || []) {
                this.applyRowWriteMetadataToColumn(child as ColDef | ColGroupDef, member, rowWriteRule);
            }
            return;
        }
        this.applyRowWriteMetadataToLeafColumn(col as ColDef, member, rowWriteRule);
    }

    private convertMemberToColumn(
        objDesc: ObjectDescription,
        member: MemberDescription,
        rowWriteRule: RowWriteRuleMetadata | null
    ): ColDef | ColGroupDef | null {
        // Don't bother displaying these fields:
        if (['Updated_By', 'Created_By', 'Created_Date'].includes(member.memberName)) {
            return null;
        }
        if (member.type === 'attachment_name') {
            // Don't display the attachment field name in its own column.
            // Instead, this will be a field used by the attachment column
            return null;
        }
        const col: ColDef = {
            field: member.memberName,
            headerName: member.displayName,
            maxWidth: 500,
            minWidth: 100,
            width: member.columnSize > 0 ? Math.max(100, Math.min(500, member.columnSize)) : undefined,
            wrapText: true,
            autoHeight: true,
            sortable: true,
            filter: CustomMultiSelectFilterComponent,
            filterParams: {
                isLocked: () => this.isReadOnlyLockedMember(member.memberName)
            },
            floatingFilterComponent: CustomMultiSelectFloatingFilterComponent,
            tooltipValueGetter: (params) => {
                const validationMessage = this.getCellValidationMessage(params?.data, col);
                if (validationMessage) {
                    return validationMessage;
                }
                if (params.data._conglomerationOutOfSyncFields?.has(col.field)) {
                    return params.data._conglomerationOutOfSyncMessage;
                }
                return params?.data?.changes?.get(col.field);
            },
            // Default valueFormatter - will be overridden for dropdowns and other special cases
            valueFormatter : (o) => {
                const value = o?.value;
                if (Array.isArray(value)) {
                    return value.join('; ');
                }
                // For objects, return empty string to avoid [object Object] display
                // Special cases (like dropdowns) will override this formatter
                if (value && typeof value === 'object') {
                    return '';
                }
                return value ?? '';
            }
        };
        const normalizedDisplayName = String(member.displayName || '').replace(/\s+/g, '').toLowerCase();
        const normalizedMemberName = String(member.memberName || '').replace(/\[\]/g, '').toLowerCase();
        const isSimilarComponentIdsField = normalizedMemberName.endsWith('similarcomponentids') ||
                                           normalizedDisplayName === 'similarcomponentids';
        const isCollectionMember = member.memberName.includes('[]') && !isSimilarComponentIdsField;

        if (isCollectionMember) {
            this.handleCollectionColumns(col, member.memberName, member.type);
        }
        if (!member.canBeMoved) {
            // This prevents this column from being moved, and not included in the col order
            col.lockPosition = true;
            col.suppressMovable = true;
        }

        if (member.displayName.toLowerCase() === 'id' || member.displayName.toLowerCase() === 'updated_date' ||
            member.isReadOnly) {
            if (member.displayName.toLowerCase() === 'id') {
                // Make the 'id' column a hyperlink to the open details view (stay keyboard-navigable; Enter opens details)
                col.cellRendererParams = {
                    ...(col.cellRendererParams || {}),
                    esciGridLink: 'viewDetails'
                };
                col.cellRenderer = (params) => {
                    const id = params.value;
                    if (!id && id !== 0) {
                        return '';
                    }
                    const link = document.createElement('a');
                    link.href = 'javascript:void(0)';
                    link.style.textDecoration = 'underline';
                    link.style.color = 'blue';
                    link.textContent = String(id);
                    link.title = 'View Details';
                    link.setAttribute('tabindex', '0');
                    const activate = () => this.onViewDetailsClick(params, false);
                    link.onclick = (ev) => {
                        ev.preventDefault();
                        activate();
                    };
                    link.addEventListener('keydown', (ev: KeyboardEvent) => {
                        if (ev.key === 'Enter') {
                            ev.preventDefault();
                            ev.stopPropagation();
                            activate();
                        }
                    });
                    return link;
                };
            } else {
                // Other read-only columns: skip Tab / keyboard cell navigation (not hyperlinks)
                col.suppressNavigable = true;
            }
            col.editable = false;
        }

        if (member.type === 'multi_select_ids' || isSimilarComponentIdsField) {
            const idOptions = this.getCurrentComponentIdOptions();
            col.valueFormatter = (o) => {
                const value = o?.value;
                if (Array.isArray(value)) {
                    return value.map(v => String(v).trim()).filter(v => v).join('; ');
                }
                if (typeof value === 'string') {
                    return value.split(/; ?/).map(v => v.trim()).filter(v => v).join('; ');
                }
                return value ?? '';
            };
            col.cellEditor = MultiSelectDropdownCellEditorComponent;
            // IDs are persisted as number[] values; disable text data-type coercion so
            // AG Grid doesn't fall back to a plain text editor.
            col.cellDataType = false;
            col.cellEditorParams = {
                availableOptions: idOptions,
                maxSelectionCount: member.maxSelectionCount,
                memberType: 'multiSelectIdsDropdown',
                isListManagerStructure: false,
                allowedValues: idOptions
            };
            col.cellEditorSelector = () => {
                // Recompute options when editing starts so AG Grid main-view editors do not
                // rely on stale column-def snapshots.
                const liveIdOptions = this.getCurrentComponentIdOptions();
                const baseParams: any = col.cellEditorParams || {};
                return {
                    component: MultiSelectDropdownCellEditorComponent,
                    params: {
                        ...baseParams,
                        availableOptions: liveIdOptions,
                        allowedValues: liveIdOptions
                    }
                };
            };
            col.cellRendererParams = {
                isListManagerStructure: false,
                allowedValues: idOptions
            };
            if (!isCollectionMember) {
                col.cellRenderer = DropdownWithDescriptionCellRendererComponent;
            }
            col.valueParser = (params: any) => this.parseMultiSelectIdsValue(params.newValue);
            col.valueSetter = (params: any) => {
                params.data[params.colDef.field] = this.parseMultiSelectIdsValue(params.newValue);
                return true;
            };
            let maxLength = 12;
            for (const option of idOptions) {
                maxLength = Math.max(maxLength, option.length);
            }
            col.minWidth = maxLength * 5 + 40;
            const similarComponentFields = this.parseDisplayTypeFields(member.fields);
            if (similarComponentFields.length > 0) {
                const group = this.buildMultiSelectIdsColumnGroup(objDesc, member, col, similarComponentFields);
                this.applyRowWriteMetadataToColumn(group, member, rowWriteRule);
                return group;
            }
            this.applyRowWriteMetadataToColumn(col, member, rowWriteRule);
            this.applyServerFilterParams(col, member);
            return col;
        }

        if (member.type === 'id_dropdown') {
            const displayFields = this.parseDisplayTypeFields(member.fields);
            if (displayFields.length === 0 || !member.listAccessKey) {
                throw new Error(`ID_DROPDOWN member '${member.memberName}' requires fields and listAccessKey`);
            }
            const group = this.buildIdDropdownColumnGroup(member, col, displayFields);
            this.applyRowWriteMetadataToColumn(group, member, rowWriteRule);
            return group;
        }

        // allowedValues is an array of objects or strings
        // For ListManager values, it's an array of { label: string, description: string } objects
        if (member.allowedValues && Array.isArray(member.allowedValues) && member.allowedValues.length > 0) {
            const allowedValues = member.allowedValues;

            // Check if this is a ListManager structure (has label and description properties)
            const isListManagerStructure = allowedValues.length > 0 &&
                                           allowedValues[0] && typeof allowedValues[0] === 'object' &&
                                           'label' in allowedValues[0] && 'description' in allowedValues[0];

            // col.cellEditor = TypeaheadCellEditorComponent;
            if (member.allowedValueKey) {
                // For object dropdowns, use the objects directly
                // The valueFormatter is used for display and change tracking
                col.valueFormatter = (o) => {
                    const value = o?.value;
                    // If value is already a string (stored in row data), return it directly
                    if (typeof value === 'string') {
                        return value;
                    }
                    // If value is an object, extract using allowedValueKey
                    if (value && typeof value === 'object' && member.allowedValueKey) {
                        const extractedValue = value[member.allowedValueKey];
                        // If the extracted value is null/undefined/empty, return empty string
                        // This handles default/empty objects that don't have the field populated
                        if (extractedValue == null || extractedValue === '') {
                            return '';
                        }
                        return String(extractedValue);
                    }
                    // If value is null/undefined or not an object, return empty string
                    return '';
                };
                col.cellEditor = ObjectDropdownCellEditorComponent;  // Use custom editor for objects
                col.cellEditorParams = {
                    availableOptions: allowedValues,
                    allowedValues: allowedValues,
                    allowedValueKey: member.allowedValueKey,
                    valueFormatString: member.valueFormatString,
                    maxSelectionCount: member.maxSelectionCount,
                    memberType: 'objectDropdown',
                    isListManagerStructure: isListManagerStructure
                };
                col.cellRendererParams = {
                    allowedValueKey: member.allowedValueKey,
                    isListManagerStructure: isListManagerStructure,
                    allowedValues: allowedValues  // Pass full array for validation
                };
                if (!isCollectionMember) {
                    col.cellRenderer = DropdownWithDescriptionCellRendererComponent;  // Use custom renderer to show description
                }
                // Add valueSetter for object dropdowns
                col.valueSetter = (params: any) => {
                    // Set the value in the row data (don't update originalRowData - that's for change tracking)
                    params.data[params.colDef.field] = params.newValue;
                    return true;
                };
            } else {
                // For string dropdowns
                let availableOptions: any[];
                if (isListManagerStructure) {
                    // For ListManager, extract labels for dropdown options
                    availableOptions = allowedValues.map((item: any) => item.label);
                } else {
                    // For regular arrays, use values directly
                    availableOptions = allowedValues;
                }
                col.cellEditor = MultiSelectDropdownCellEditorComponent;  // Use custom editor
                col.cellDataType = 'text';  // Explicitly tell ag-grid this column contains text/string data
                col.cellEditorParams = {
                    availableOptions: availableOptions,
                    maxSelectionCount: member.maxSelectionCount,
                    memberType: 'multiSelectDropdown',
                    isListManagerStructure: isListManagerStructure,
                    allowedValues: allowedValues  // Pass full array for descriptions
                };
                col.cellRendererParams = {
                    isListManagerStructure: isListManagerStructure,
                    allowedValues: allowedValues  // Pass full array for descriptions
                };
                if (!isCollectionMember) {
                    col.cellRenderer = DropdownWithDescriptionCellRendererComponent;  // Use custom renderer to show description
                }
                // Add valueParser to ensure the value is always stored as a string
                // This prevents ag-grid data type mismatch errors
                col.valueParser = (params: any) => {
                    const newValue = params.newValue;
                    // Ensure we always return a string (or null/undefined)
                    if (newValue == null) {
                        return null;
                    }
                    return String(newValue);
                };
                // Ensure the value is properly set when the editor returns a value
                col.valueSetter = (params: any) => {
                    params.data[params.colDef.field] = params.newValue;
                    return true;
                };
            }
            let maxLength = 12; // sets a default minimum column width
            for (const value of allowedValues) {
                if (isListManagerStructure && value.label) {
                    maxLength = Math.max(maxLength, value.label.length);
                } else if (typeof value === 'string') {
                    maxLength = Math.max(maxLength, value.length);
                } else if (member.allowedValueKey && value[member.allowedValueKey]) {
                    maxLength = Math.max(maxLength, value[member.allowedValueKey].length);
                }
            }
            col.minWidth = maxLength * 5 + 40;
            if (member.maxSelectionCount > 1) {
                col.minWidth *= 3;
            }
        }
        if (member.type === 'conglomeration' && member.fields) {
            col.cellEditorParams = { memberType: 'conglomeration', conglomerationFields: member.fields };
            col.valueGetter = (params) => {
                if (params.data._conglomerationOutOfSyncFields?.has(col.field)) {
                    return params.data[col.field];
                }
                return buildConglomerationFromRow(params.data, member.fields);
            };
            col.valueSetter = (params: any) => {
                parseConglomerationToRow(params.data, member.fields, params.newValue);
                params.data[params.colDef.field] = params.newValue;
                this.clearConglomerationOutOfSyncForRow(params.data, member.memberName, member.fields);
                params.api?.refreshCells({ rowNodes: [params.node], force: true });
                return true;
            };
        }
        if (member.type === 'image_list' && !isCollectionMember) {
            const group = this.buildPartImageColumnGroup(member, col);
            this.applyRowWriteMetadataToColumn(group, member, rowWriteRule);
            return group;
        }
        if ((member.type === 'image') || (member.type === 'attachment')) {
            const attachmentParams: Record<string, unknown> = {
                urlBase: environment.apiUrl + '/esciUI/filedata/' + this.componentName,
                attachmentChanged: this.onAttachmentChanged.bind(this),
                memberType: member.type
            };
            if (member.type === 'attachment') {
                attachmentParams.fileIdField = member.memberName;
                const isValidationReportField = member.memberName === 'validationReportFileId' ||
                    member.memberName.endsWith('.validationReportFileId') ||
                    member.memberName.endsWith('].validationReportFileId');
                if (isValidationReportField) {
                    attachmentParams.nameField = 'validationReportName';
                    attachmentParams.pendingFileProperty = 'validationReportFile';
                    attachmentParams.defaultDisplayName = 'Validation Report';
                    attachmentParams.componentIdField = 'componentId';
                }
            }
            col.cellEditorParams = attachmentParams;
            const comp = (member.type === 'image') ? ImageCellRenderComponent : AttachmentCellRendererComponent;
            col.cellEditor = comp;
            if (!isCollectionMember) {
                col.cellRendererParams = attachmentParams;
                col.cellRenderer = comp;
                col.filter = null;
                col.sortable = false;
                col.maxWidth = col.minWidth = 140;
            }
        }
        if (member.type === 'part_number_with_smart_search') {
            col.cellRendererParams = {
                urlBase: objDesc.partNumberSmartSearchUrl
            };
            col.cellRenderer = UrlCellRendererComponent;
            // Explicit text editor so commit/stopEditing (including custom Tab handling) reliably runs
            // cellEditingStopped; renderer-only columns can omit stable edit lifecycle in some AG Grid versions.
            col.cellEditor = 'agTextCellEditor';
            col.maxWidth = col.minWidth = 140;
        }
        if (member.type === 'url') {
            col.cellRendererParams = {
                useValueAsUrl: true
            };
            if (!isCollectionMember) {
                col.cellRenderer = UrlCellRendererComponent;
            }
            col.cellEditor = 'agTextCellEditor';
        }
        if (member.type === 'date' || member.type === 'timestamp') {
            col.valueFormatter = (o) => o?.value && CommonHelperService.formatDate(o.value);
            col.cellEditor = 'agDateCellEditor';
            col.cellEditorParams = {
                dateFormat: 'yyyy-MM-dd',
                memberType: 'date'
            };
        }
        if (['integer', 'decimal', 'big_decimal'].includes(member.type)) {
            col.cellEditor = NumberCellEditor;
            col.cellEditorParams = {
                memberType: 'number'
            };
            col.valueParser = (params) => {
                // Parse the input value to a number with a fixed number of decimal places
                const value = parseFloat(params.newValue);
                return isNaN(value) ? null : parseFloat(value.toFixed(member.maxDecimalPlaces));
            };
            col.valueFormatter = (params) => {
                const value = params.value;
                // Format the value to display the fixed number of decimal places
                if (typeof value === 'number' && !isNaN(value)) {
                    return parseFloat(value.toFixed(member.maxDecimalPlaces).toString()).toString();
                }
                return '';
            };
        }
        if (member.type === 'gmin') {
            col.cellEditor = UserSearchCellEditorComponent;  // Use custom editor
            col.valueGetter = (params) => this.getValueFromUser(params.data[col.field]);
            col.filterValueGetter = (params) => this.getValueFromUser(params.data[col.field]);
            col.cellRenderer = UserSearchCellEditorComponent;
            col.cellRendererParams = {
                userField: col.field,
                readonly: true
            };
            col.cellEditorParams = {
                onSelectedGmin: this.onSelectedGmin.bind(this),
                memberType: 'userSearch'
            };
            col.maxWidth = col.minWidth = col.width = 270;
        }

        const fullPath = objDesc.displayName + '.' + member.memberName;
        if (member.valueFormatString) {
            // Use server-provided format string
            col.valueFormatter = (o => {
                if (!o || o.value == null) {
                    return '';
                }
                // Evaluate the format string with 'val' as the context variable
                return CommonHelperService.evaluateFormatString(member.valueFormatString, { val: o.value });
            });
        } else if (fullPath === 'Grommet.holeCriteria' || fullPath === 'Clip.holeCriteria') {
            col.cellRenderer = (o => {
                if (!o?.value) {
                    return '';
                }
                let results = '<table style="font-size: xx-small"><tbody style="word-break: break-word"><tr>' +
                                  '<th style="min-width: 50px">Label</th>' +
                                  '<th style="min-width: 90px; text-align:center" colspan="3">Hole Size (mm)</th>' +
                                  '<th style="min-width: 50px">Hole Feature Number</th>' +
                                  '<th>Connector Part Numbers</th>' +
                              '</tr>';
                for (const row of o.value?.split('|') || []) {
                    results += '<tr>';
                    let colIndex = 0;
                    for (const column of row.split('\\')) {
                        if (colIndex++ === 1) {
                            const holeSizes = column.split('x');
                            results += `<td style="text-align: right">${holeSizes[0]}</td>`;
                            results += `<td style="width: 8px; min-width:unset; text-align: center;">x</td>`;
                            results += `<td style="text-align: left">${holeSizes[1]}</td>`;
                        } else {
                            results += `<td>${column.replace(/,/g, ', ')}</td>`;
                        }
                    }
                    results += '</tr>';
                }
                return results + '</tbody></table>';
            });
            col.cellEditor = HoleCriteriaEditorComponent;
            col.cellEditorParams = {
                memberType: 'holeCriteria'
            };
            col.maxWidth = col.minWidth = col.width = 750;
        } else if (col.minWidth > 400 &&
                   !(member.allowedValues && Array.isArray(member.allowedValues) && member.allowedValues.length > 0)) {
            // when (member.allowedValues?.length) is true, the minWidth was adjusted to fit the width of the large
            // value allowed in the dropdown, so we don't want to convert this into a wide cell editor here in that case.
            col.cellEditor = 'agLargeTextCellEditor';
            col.cellEditorParams = {
                maxLength: member.columnSize,
                rows: 5,
                cols: 80,
                memberType: 'largeText'
            };
            col.cellRenderer = (params) => {
                const value = params?.value;
                if (typeof value === 'string') {
                    // Replace \n with <br> for rendering newlines
                    return value.replace(/\n/g, '<br>');
                }
                if (Array.isArray(value)) {
                    return value.join(', ');
                }
                if (value && typeof value === 'object') {
                    return '';
                }
                return value ?? '';
            };
            // Ensure text wraps and respects newlines. and is centered vertically
            col.cellStyle = { display: 'flex', alignItems: 'center', whiteSpace: 'pre-wrap' };
        }

        // So details view can rebuild conglomeration when a sub-field is edited
        if (member.parentConglomeration?.fields) {
            col.cellEditorParams = col.cellEditorParams || {};
            (col.cellEditorParams as any).parentConglomeration = {
                memberName: member.parentConglomeration.memberName,
                conglomerationFields: member.parentConglomeration.fields
            };
        }

        this.applyRowWriteMetadataToColumn(col, member, rowWriteRule);
        this.applyServerFilterParams(col, member);
        return col;
    }

    /**
     * Under server paging the grid holds one page, so a column's filter and sort must run in the database
     * or not at all. A column the query can match gets its filter values from the server and pushes its
     * selection into the query; a column the query cannot order by loses its sort. Both are decided per
     * column, since a value reached through a collection can be matched without being sortable.
     */
    private applyServerFilterParams(col: ColDef | ColGroupDef, member: MemberDescription): void {
        if ('children' in col) {
            return;
        }
        if (!this.serverPaged()) {
            // Client mode: the whole list is in the browser, so AG Grid's own native filter/sort handles every
            // column itself; nothing here needs restricting to what a server query can reach.
            return;
        }
        const leaf = col as ColDef;
        if (!member.serverSortable) {
            leaf.sortable = false;
        }
        if (!member.serverFilterable) {
            leaf.filter = false;
            leaf.floatingFilter = false;
            return;
        }
        leaf.filterParams = {
            ...(leaf.filterParams as object || {}),
            serverSideFiltering: true,
            distinctValuesProvider: () => this.esciService.getObjectColumnValues(
                this.componentName, member.serverQueryMember || member.memberName)
        };
    }

    private buildPartImageColumnGroup(member: MemberDescription, baseCol: ColDef): ColDef | ColGroupDef {
        if (this.componentImageTypes.length === 0) {
            return baseCol;
        }
        const children = this.componentImageTypes.map((imageType: ComponentImageType): ColDef => {
            const rendererParams = {
                urlBase: environment.apiUrl + '/esciUI/filedata/' + this.componentName,
                attachmentChanged: this.onAttachmentChanged.bind(this),
                memberType: member.type,
                imageName: imageType,
                imageTypes: this.componentImageTypes
            };
            return {
                field: member.memberName,
                colId: `${member.memberName}__${imageType}`,
                headerName: imageType,
                editable: baseCol.editable,
                lockPosition: baseCol.lockPosition,
                suppressMovable: baseCol.suppressMovable,
                width: 140,
                minWidth: 140,
                maxWidth: 240,
                filter: null,
                sortable: false,
                cellRenderer: MultiImageCellRenderComponent,
                cellEditor: MultiImageCellRenderComponent,
                cellRendererParams: rendererParams,
                cellEditorParams: rendererParams,
                valueGetter: params => getComponentImageIdsForType(
                    params.data?.[member.memberName], imageType, this.componentImageTypes),
                valueSetter: params => {
                    params.data[member.memberName] =
                        setComponentImageIdsForType(
                            params.data[member.memberName], imageType, params.newValue ?? [], this.componentImageTypes);
                    return true;
                }
            };
        });
        return {
            headerName: member.displayName,
            groupId: member.memberName,
            marryChildren: true,
            children
        };
    }

    private parseDisplayTypeFields(fieldsSpec: string | undefined): string[] {
        if (!fieldsSpec) {
            return [];
        }
        return fieldsSpec.split(',')
                         .map((token: string) => token.trim())
                         .filter((token: string) => token !== '' && token !== '-');
    }

    private buildMultiSelectIdsColumnGroup(objDesc: ObjectDescription,
                                           member: MemberDescription,
                                           baseCol: ColDef,
                                           displayFields: string[]): ColGroupDef {
        const childColumns = displayFields.map((displayField: string) => {
            const fieldDef = objDesc.members?.find((candidate: MemberDescription) =>
                candidate.memberName === displayField);
            const normalizedWidth = fieldDef?.columnSize
                ? Math.max(120, Math.min(500, fieldDef.columnSize))
                : 140;
            const childCol: ColDef = {
                field: displayField,
                colId: `${member.memberName}__${displayField}`,
                headerName: fieldDef?.displayName || this.humanizeFieldName(displayField),
                width: normalizedWidth,
                minWidth: 120,
                maxWidth: 500,
                editable: false,
                sortable: false,
                filter: false,
                suppressNavigable: true,
                autoHeight: true,
                cellClass: 'subrow-cell',
                valueGetter: (params: any) =>
                    this.getMultiSelectIdsResolvedValues(params?.data, member.memberName, displayField),
                valueFormatter: (params: any) =>
                    Array.isArray(params?.value) ? params.value.join('; ') : (params?.value ?? ''),
                cellRenderer: (params: any) => {
                    const values = Array.isArray(params?.value) ? params.value : [];
                    return values.map((value: any) => `<div class="subrow">${value ?? ''}</div>`).join('');
                }
            };
            (childCol as any).esciPersistKey = `${member.memberName}::${displayField}`;
            // Child columns reuse the real member's field name (e.g. "supplier") purely for value
            // resolution/persistence keys, so change-tracking logic must not treat them as the same
            // column as the real editable field with that name - see esciDerivedGroupChildField usage
            // in editable-grid-component.ts's cellClassRules.
            (childCol as any).esciDerivedGroupChildField = true;
            return childCol;
        });

        const group: ColGroupDef = {
            headerName: baseCol.headerName,
            groupId: `${member.memberName}__multi_select_ids`,
            marryChildren: true,
            children: childColumns
        };
        (group as any).esciDerivedRowsResolver = (sourceRow: any) =>
            this.resolveMultiSelectIdsRows(sourceRow, member.memberName, displayFields);
        (group as any).esciDerivedRowsReadOnly = true;
        (group as any).esciDerivedRowsMode = 'multiSelectIds';
        (group as any).esciIdsFieldName = member.memberName;
        (group as any).esciAvailableIdsResolver = () => this.getCurrentComponentSelectOptions();
        (group as any).esciIdOptionLabelFields = displayFields.filter(field => field !== 'id');
        // A server-paged grid holds one page, so its picker searches the catalog on the server as the user types. A
        // client-mode grid already holds every row, so its picker is left to filter those - with no search wired,
        // the child grid filters the available options locally, and with no minimum length or row cap. Wiring the
        // search regardless would put every catalog the configuration does not name behind both.
        if (objDesc?.serverPaged) {
            (group as any).esciSearchIdOptions = (searchText: string) => this.searchComponentIdOptions(searchText);
        }
        return group;
    }

    private buildIdDropdownColumnGroup(member: MemberDescription,
                                       baseCol: ColDef,
                                       displayFields: string[]): ColGroupDef {
        let pickerRowsById = new Map<number, any>();
        this.esciService.getComponentIdPickerRows(member.listAccessKey).subscribe((rows: any[]) => {
            pickerRowsById = new Map(
                (Array.isArray(rows) ? rows : [])
                    .filter((row: any) => Number.isFinite(Number(row?.id)))
                    .map((row: any) => [Number(row.id), row])
            );
            this.gridApi?.refreshCells({force: true});
        });
        const relativeIdField = member.memberName.split('[]').pop()?.replace(/^\./, '') || member.memberName;
        const pickColumn: ColDef = {
            field: member.memberName,
            colId: `${member.memberName}__pick`,
            headerName: 'Pick',
            width: 90,
            minWidth: 90,
            maxWidth: 90,
            editable: false,
            sortable: false,
            filter: false,
            suppressNavigable: true,
            hide: true,
            cellRenderer: (params: any) => {
                if (!params?.data || CommonHelperService.getDeepValue(params.data, relativeIdField) === undefined) {
                    return '';
                }
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'p-button p-component p-button-sm gm-btn-primary';
                button.textContent = 'Pick';
                button.addEventListener('click', (event: MouseEvent) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.openComponentIdPicker(
                        member,
                        displayFields,
                        (selectedId: number | null) => params?.setValue?.(selectedId));
                });
                return button;
            }
        };
        (pickColumn as any).esciDetailOnly = true;
        const displayColumns = displayFields.map((displayField: string) => ({
            colId: `${member.memberName}__${displayField}`,
            headerName: this.humanizeFieldName(displayField),
            minWidth: 120,
            maxWidth: 500,
            editable: false,
            sortable: false,
            filter: false,
            suppressNavigable: true,
            cellRenderer: (params: any) => {
                const container = document.createElement('div');
                const ids = this.getIdDropdownValues(params?.data, member.memberName, relativeIdField);
                for (const id of ids) {
                    const row = pickerRowsById.get(id);
                    const value = row?.[displayField] ?? '';
                    const line = document.createElement('div');
                    line.className = 'subrow';
                    line.textContent = String(value);
                    container.appendChild(line);
                }
                return container;
            }
        } as ColDef));
        return {
            headerName: baseCol.headerName,
            groupId: `${member.memberName}__id_dropdown`,
            marryChildren: true,
            children: [pickColumn, ...displayColumns]
        };
    }

    private getIdDropdownValues(row: any, fullPath: string, relativePath: string): number[] {
        const directValue = CommonHelperService.getDeepValue(row, relativePath);
        const values = directValue !== undefined
            ? [directValue]
            : this.getNestedArrayWithPlaceholders(row, fullPath);
        return values.map((value: any) => Number(value))
            .filter((value: number) => Number.isFinite(value) && value > 0);
    }

    private openComponentIdPicker(member: MemberDescription, displayFields: string[],
                                  applySelectedId: (selectedId: number | null) => void): void {
        this.esciService.getComponentIdPickerRows(member.listAccessKey).pipe(take(1)).subscribe((rows: any[]) => {
            const displayName = String(member.displayName || '').trim();
            const hasIdSuffix = displayName.length > 2 &&
                displayName.slice(-2).toLowerCase() === 'id' &&
                displayName.at(-3)?.trim() === '';
            const pickerName = hasIdSuffix ? displayName.slice(0, -2).trimEnd() : displayName;
            const pickerRef = this.dialogService.open(ComponentIdPickerDialogComponent, {
                header: `Select ${pickerName}`,
                width: '75vw',
                data: {rows, fields: displayFields}
            });
            pickerRef.onClose.subscribe((selectedRow: any) => {
                if (selectedRow === null) {
                    applySelectedId(null);
                    return;
                }
                const selectedId = Number(selectedRow?.id);
                if (Number.isFinite(selectedId) && selectedId > 0) {
                    applySelectedId(selectedId);
                }
            });
        });
    }

    private humanizeFieldName(fieldName: string): string {
        const normalized = String(fieldName || '')
            .replace(/\[\]/g, '')
            .replace(/_/g, ' ')
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .trim();
        if (!normalized) {
            return '';
        }
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }

    private getMultiSelectIdsResolvedValues(row: any, idsFieldName: string, targetFieldName: string): any[] {
        const resolvedRows = this.resolveMultiSelectIdsRows(row, idsFieldName, [targetFieldName]);
        return resolvedRows.map((resolvedRow: any) => resolvedRow?.[targetFieldName]);
    }

    /**
     * One sub-row per id the field names. An id whose row is not to hand - deleted, restricted, or a lookup that
     * failed - still gets its sub-row, with nothing in its display columns: dropping it would show a row with three
     * similar components as having two, and the count is the part a user is most likely to rely on.
     *
     * <p>A component just chosen from the picker is none of those things: the search that offered it returned its
     * labels, but it is not on the page and no lookup has asked for it, so the row caches cannot answer for it yet.
     * Those labels stand in until one can. They are a caption rather than a row, so they are read here instead of
     * being added to what getCurrentRowById answers with - an export reading every other column off a two-field
     * object would write a workbook of blanks.
     */
    private resolveMultiSelectIdsRows(row: any, idsFieldName: string, displayFields: string[]): any[] {
        const selectedIds = this.parseMultiSelectIdsValue(row?.[idsFieldName]);
        const resolvedRows: any[] = [];
        for (const selectedId of selectedIds) {
            const matchingRow = this.getCurrentRowById(selectedId);
            const searchedLabels = matchingRow ? null : this.componentIdOptions.get(selectedId)?.labels;
            const flattened: any = {};
            flattened.__selectedId = selectedId;
            for (const displayField of displayFields) {
                // The id is the one value always known, and the searched labels never carry it - without it a
                // deleted component's sub-row is blank in every column and cannot be told apart from another.
                flattened[displayField] = matchingRow
                                          ? CommonHelperService.getDeepValue(matchingRow, displayField)
                                          : (displayField === 'id' ? selectedId
                                                                   : (searchedLabels?.[displayField] ?? ''));
            }
            resolvedRows.push(flattened);
        }
        return resolvedRows;
    }

    private getCurrentRowById(id: number): ValidationModel | undefined {
        const onPage = (this.rowData || []).find((candidate: any) => Number(candidate?.id) === id);
        // A row may point at a component on another page, whose values were fetched by id.
        return this.exportRowsById?.get(id) ?? onPage ?? this.offPageRowsById.get(id);
    }

    handleCollectionColumns(col: ColDef, path: string, memberType?: string) : void {
        col.cellRenderer = (params) => {
            const rowId = params?.data?.id;
            const originalRowId = params?.data?.originalRowData?.id;
            const compId = rowId === -1 && originalRowId != null && originalRowId !== -1 ? originalRowId : rowId;
            const isAttachmentCollection = memberType === 'attachment';
            const attachmentUrlBase = `${environment.apiUrl}/esciUI/filedata/${this.componentName}`;
            if (isAttachmentCollection) {
                const attachmentRows = this.getCollectionAttachmentRows(params?.data, path);
                return attachmentRows.map((attachmentRow: { fileId: number; displayName: string }) => {
                    if (Number.isFinite(attachmentRow.fileId) && attachmentRow.fileId > 0 && Number(compId) > 0) {
                        const href = `${attachmentUrlBase}/${compId}/${attachmentRow.fileId}`;
                        return `<div class="subrow"><a href="${href}" target="_blank" rel="noopener noreferrer">${escapeHtml(attachmentRow.displayName)}</a></div>`;
                    }
                    if (attachmentRow.displayName && attachmentRow.displayName.trim().length > 0) {
                        return `<div class="subrow">${escapeHtml(attachmentRow.displayName)}</div>`;
                    }
                    return '<div class="subrow"></div>';
                }).join('');
            }
            const list = this.getNestedArrayWithPlaceholders(params.data, path);
            if (!Array.isArray(list)) {
                return '';
            }
            return list.map(val => {
                const formatted = col.valueFormatter instanceof Function
                    ? col.valueFormatter({ value: val, data: params?.data, colDef: col } as any)
                    : val;
                const text = String(formatted ?? '');
                if (memberType === 'url' && isSafeWebUrl(text)) {
                    const escapedUrl = escapeHtml(text);
                    return `<div class="subrow"><a href="${escapedUrl}" target="_blank" ` +
                        `rel="noopener noreferrer">${escapedUrl}</a></div>`;
                }
                return `<div class="subrow">${escapeHtml(text)}</div>`;
            }).join('');
        };
        col.cellClass = 'subrow-cell'; // CSS class for subrows
        col.editable = false; // We cannot edit these directly because they are arrays. Use the details view to edit.
        col.suppressNavigable = true;
        col.autoHeight = true;
    }

    private getCollectionAttachmentRows(rowData: any, path: string): Array<{ fileId: number; displayName: string }> {
        if (!path?.includes('[]')) {
            const fileId = Number(CommonHelperService.getDeepValue(rowData, path));
            return [{ fileId, displayName: Number.isFinite(fileId) && fileId > 0 ? String(fileId) : '' }];
        }
        const [arrayPath, remainderPathRaw] = path.split('[]');
        const collectionRows = CommonHelperService.getDeepValue(rowData, arrayPath);
        if (!Array.isArray(collectionRows)) {
            return [];
        }
        const fileIdPath = remainderPathRaw?.startsWith('.') ? remainderPathRaw.substring(1) : remainderPathRaw;
        const attachmentNameField = fileIdPath?.endsWith('FileId')
            ? fileIdPath.replace(/FileId$/, 'Name')
            : null;
        return collectionRows.map((entry: any) => {
            const fileId = Number(fileIdPath ? CommonHelperService.getDeepValue(entry, fileIdPath) : entry);
            const attachmentName = attachmentNameField ? CommonHelperService.getDeepValue(entry, attachmentNameField) : null;
            const displayName = typeof attachmentName === 'string' && attachmentName.trim().length > 0
                ? attachmentName.trim()
                : (Number.isFinite(fileId) && fileId > 0 ? String(fileId) : '');
            return { fileId, displayName };
        });
    }

    private getNestedArrayWithPlaceholders(obj: any, path: string): any[] {
        if (!path?.includes('[]')) {
            return this.getNestedArray(obj, path);
        }
        const [arrayPath, remainderPathRaw] = path.split('[]');
        const arrayRoot = CommonHelperService.getDeepValue(obj, arrayPath);
        if (!Array.isArray(arrayRoot)) {
            return [];
        }
        const remainderPath = remainderPathRaw?.startsWith('.') ? remainderPathRaw.substring(1) : remainderPathRaw;
        return arrayRoot.map((entry: any) => {
            if (!remainderPath) {
                return entry ?? '';
            }
            const value = CommonHelperService.getDeepValue(entry, remainderPath);
            return value === undefined || value === null ? '' : value;
        });
    }

    /**
     * Walk an object following a dotted path that may include "[]"
     * to indicate arrays. Returns a flat array of resolved values.
     *
     * Examples:
     *   getNestedArray(data, 'approvedTerminalPn')                     => ['12345678']
     *   getNestedArray(data, 'cavityGroups[].id')                      => ['12','13','15']
     *   getNestedArray(data, 'cavityGroups[].wireSizeMax.isoWireSize') => ['SizeA','SizeB','SizeC']
     */
    getNestedArray(obj: any, path: string): any[] {
        if (!obj || !path) {
            return [];
        }
        const deepValue = CommonHelperService.getDeepValue(obj, path);
        if (Array.isArray(deepValue)) {
            return this.flattenNestedArray(deepValue);
        }
        return deepValue === undefined ? [] : [deepValue];
    }

    private flattenNestedArray(value: any[]): any[] {
        const flattened: any[] = [];
        const flatten = (current: any) => {
            if (Array.isArray(current)) {
                for (const entry of current) {
                    flatten(entry);
                }
                return;
            }
            if (current !== undefined) {
                flattened.push(current);
            }
        };
        flatten(value);
        return flattened;
    }

    loadComponentData(): void {
        const loadingFor = this.componentName;
        const loadGeneration = ++this.componentDataGeneration;
        const isCurrentLoad = () => !this.destroyed() && loadGeneration === this.componentDataGeneration;
        forkJoin({
            objDesc: this.esciService.getObjectDescription(this.componentName),
            imageTypes: this.esciService.getComponentImageTypes(this.componentName).pipe(
                catchError(() => of([] as ComponentImageType[]))
            ),
            defaultGridFilters: this.esciService.getDefaultGridFilters().pipe(
                catchError(() => of({} as Record<string, string[]>))
            )
        }).subscribe({
            next: ({objDesc, imageTypes, defaultGridFilters}) => {
                // The generation also distinguishes A -> B -> A navigation, where a name-only check
                // would accept the first A response after the second A request had already started.
                if (!isCurrentLoad() || loadingFor !== this.componentName) {
                    return;
                }
                this.defaultGridFilters.set(defaultGridFilters || {});
                this.componentImageTypes = imageTypes;
                this.objectDescription = objDesc;
                this.tryOpenReadOnlyAdvancedFilter();
                this.serverPaged.set(!!objDesc?.serverPaged);
                this.resetServerPagingState();
                // Find the member marked as forDelete which comes from DisplayFilter.java
                // EX: @DisplayFilter(value = "Reference Data/Controller Designator", forDelete = true)
                this.fieldForDeleteMessage =
                    objDesc?.members?.find(member => member?.isFieldForDeleteMessage)?.memberName || 'id';

                // Update column definitions
                const actionCol: ColDef = {
                    minWidth: 88,
                    maxWidth: 88,
                    headerName: 'Actions',
                    colId: 'Actions',
                    cellClass: 'actions-column-cell',
                    rowDrag: false,
                    // Keep Tab/keyboard nav on data cells; avoid jumping to row action buttons (copy, etc.).
                    suppressNavigable: true,
                    cellRenderer: ActionsCellRendererComponent,
                    cellRendererParams: {
                        onDeleteClick: (function (params: any): void {
                            this.onDeleteClick(params, this.rowData, (updatedData) => this.refreshGrid(updatedData));
                        }).bind(this),
                        onRevertClick: this.onRevertClick.bind(this),
                        onSaveClick: this.onSaveClick.bind(this),
                        onViewDetailsClick: this.onViewDetailsClick.bind(this),
                        onCopyClick: this.onCopyClick.bind(this),
                        fieldForDeleteMessage: this.fieldForDeleteMessage,
                        isApprovedLockedRow: this.isApprovedLockedRow.bind(this)
                    },
                    editable: false,
                    filter: false,
                    sortable: false,
                    pinned: 'left',
                    lockPinned: true,
                    suppressMovable: true
                };
                // don't allow delete operations on RingTerminal:
                if (this.componentName === 'RingTerminal') {
                    delete actionCol.cellRendererParams.onDeleteClick;
                }

                const newCols = this.convertObjectDescriptionToColumns(objDesc);
                newCols.unshift(actionCol);

                // Add "Kit Relationships" column for DiagTestProbe and TerminalReleaseTool
                if (this.componentName === 'DiagTestProbe' || this.componentName === 'TerminalReleaseTool') {
                    newCols.push(this.createKitRelationshipsColumn());
                }

                this.queueColumnUpdate(newCols, loadingFor, loadGeneration);
            }, error: (err: any) => {
                if (!isCurrentLoad() || loadingFor !== this.componentName) {
                    return;
                }
                console.error(err);
                this.messageService.processError(err?.error?.message || err?.message || err);
            }
        });
        // getData() is called after both the object description and configured defaults are available.
    }

    private queueColumnUpdate(newCols: (ColDef | ColGroupDef)[], loadingFor: string, generation: number): void {
        this.pendingColumnDefs.set({loadingFor, generation, newCols});
        afterNextRender(() => {
            const pending = this.pendingColumnDefs();
            if (!pending || pending.loadingFor !== loadingFor || pending.generation !== generation) {
                return;
            }
            this.pendingColumnDefs.set(null);
            if (this.destroyed() || generation !== this.componentDataGeneration ||
                pending.loadingFor !== this.componentName) {
                return;
            }
            this.replaceCols(pending.newCols);
            this.sortColumns();
            this.invalidateFlatColsCache(); // Columns changed, invalidate cache
            // Apply the new column state to the grid (use saved widths when available). Re-run the same
            // layout sequence onUserDataLoaded() runs for the very first catalog: applyComponentSwitch()
            // turns columnPersistReady off on every switch, and onUserDataLoaded() only fires once (when
            // fetchPersistentUserData() first resolves), so without this a later catalog's own column
            // moves/resizes would never be marked ready to persist again. Each of these three calls is
            // already a no-op until persistentUserData exists, so calling them here is safe even before
            // the very first fetchPersistentUserData() response has arrived.
            if (this.gridApi) {
                this.sizeColumnsToFitIfInitialLoad();
                this.applySavedColumnWidthsToGrid();
                this.markColumnPersistenceReadyAfterLayout();
            }
            this.setColumnsVisible();
            // Load data after columns are ready so we have objectDescription when applying URL params
            this.componentColumnsReady = true;
            this.getData();
        }, {injector: this.injector});
    }

    private replaceCols(newCols: (ColDef | ColGroupDef)[]): void {
        this.cols.length = 0;
        this.cols.push(...newCols);
        // Refresh feature-presence flags from the resolved column set rather than keying off a
        // hardcoded component-name list, so e.g. adding a tier_n_resilient_id column to a new
        // Component on the backend automatically exposes the matching grid affordances here.
        this.hasPPCO = CommonHelperService.hasFieldInColumns(this.cols, 'ppco');
        this.hasTierNResilient = CommonHelperService.hasFieldInColumns(this.cols, 'tierNResilient');
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.setGridOption('columnDefs', this.cols);
        }
    }

    private flattenColDefs(columns: (ColDef | ColGroupDef)[]): ColDef[] {
        // Return cached version if available and we're flattening this.cols (the main columns array)
        // Note: We only cache when flattening this.cols, not when recursively flattening child columns
        const isMainCols = columns === this.cols;
        if (isMainCols && this.cachedFlatCols !== null) {
            return this.cachedFlatCols;
        }

        const result: ColDef[] = [];
        for (const col of columns) {
            if ('children' in col) {
                // It's a ColGroupDef - recursively flatten children
                result.push(...this.flattenColDefs(col.children));
            } else {
                // It's a ColDef
                result.push(col);
            }
        }

        // Only cache if this was the main columns array
        if (isMainCols) {
            this.cachedFlatCols = result;
        }
        return result;
    }

    /**
     * Invalidates the cached flattened column definitions.
     * Call this whenever columns are modified to ensure cache consistency.
     */
    private invalidateFlatColsCache(): void {
        this.cachedFlatCols = null;
    }

    private onSelectedGmin(params, newUser, field): void {
        params.data[field] = newUser;
    }

    private convertObjectDescriptorIntoFieldRule(objectDescription: any): FieldRule {
        let maxLength = objectDescription.columnSize;
        let type = 'string';
        let pattern = null;
        let patternName = null;
        const allowsBlankValue = this.objectDescriptionAllowsBlankValue(objectDescription);
        // Required fields must not treat blank dropdown labels as valid input.
        const effectiveAllowsBlankValue = allowsBlankValue && !objectDescription.isRequired;
        // Narrowly scoped exception: if THIS field's own allowedValues contains a real DB-backed
        // reference option (positive id) whose display value is blank - e.g. RingTerminal's Plating
        // id=41 resolves to plating: "" - selecting that specific reference legitimately satisfies
        // "required" via object identity. Unlike allowsBlankValue above, this must never be forced off
        // for required fields, and it never applies to other columns whose own data has no such entry.
        const allowsBlankIdBackedReference = this.objectDescriptionAllowsIdBackedBlankValue(objectDescription);
        const maxAllowedDisplayLength = this.getMaxAllowedDisplayLength(objectDescription);
        switch (objectDescription.type) {
            case 'varchar':
            case 'char':
            case 'url':
                break;
            case 'part_number_with_smart_search':
                pattern = /^\d{8}$/;
                patternName = '8 digits';
                maxLength = objectDescription.maxValue;
                type = 'number';
                break;
            case 'integer':
                let maxDigits = objectDescription.columnSize;
                if (objectDescription.maxValue) {
                    maxDigits = objectDescription.maxValue;
                }
                pattern = new RegExp(`^-?\\d{0,${maxDigits}}$`);
                patternName = `no more than ${maxDigits} digits`;
                // fallthrough to set the 'maxLength' to 0 (potentially) because the maxLength for integer is not
                // character length, its maximum value. We've already set up the pattern to enforce character length.
            // eslint-disable-next-line no-fallthrough
            case 'decimal':
            case 'big_decimal':
                maxLength = objectDescription.maxValue;
                type = 'number';
                break;
            case 'boolean':
                maxLength = 3;
                break;
            case 'timestamp':
            case 'date':
                // Still build a rule (below) instead of skipping the field entirely - a NOTNULL date/timestamp
                // column (e.g. componentToolingInfo[].toolAmortizationDate) must be enforced as required just
                // like any other type, including when nested inside a child-collection member path.
                type = 'date';
                maxLength = null;
                break;
            case 'blob':
            case 'clob':
                return null;
        }
        if ((objectDescription.type === 'dropdown' || objectDescription.type === 'multi_select') &&
            maxAllowedDisplayLength > 0) {
            maxLength = Math.max(maxLength ?? 0, maxAllowedDisplayLength);
        }
        if (objectDescription.isRequired && type !== 'date') {
            // If the field is required, we need to ensure that the pattern is not null
            if (!pattern) {
                pattern =  /^(?!\s*$).+/; // Non-empty string and not only whitespace
                patternName = 'Non-empty';
            }
        }

        if (objectDescription.isReadOnly) {
            return {
                fieldName: objectDescription.memberName,
                required: false,
                uniqueFieldSetName: '',
                maxLength: 0,
                pattern: null,
                patternName: null,
                type: type,
                allowedValueKey: objectDescription.allowedValueKey,
                allowsBlankValue: effectiveAllowsBlankValue,
                allowsBlankIdBackedReference
            };
        }
        return {
            fieldName: objectDescription.memberName,
            required: objectDescription.isRequired,
            // When this is a nested object (it has dots in the memberName), then we don't consider uniqueness
            // This is because we often have references to things like programs, and the program may declare that
            // it's "id" is unique, but multiple objects may refer to the same program, and that doesn't violate
            // the uniqueness constraint of the parent object.
            uniqueFieldSetName: objectDescription.memberName.includes(".") ? '' : objectDescription.uniqueFieldSetName,
            // For numeric fields, this is really max value
            // For string fields, this is max character length and should match db column
            maxLength, pattern, patternName, type,
            allowedValueKey: objectDescription.allowedValueKey,
            allowsBlankValue: effectiveAllowsBlankValue,
            allowsBlankIdBackedReference
        };
    }

    private objectDescriptionAllowsBlankValue(objectDescription: any): boolean {
        const allowedValues = objectDescription?.allowedValues;
        if (!Array.isArray(allowedValues) || allowedValues.length === 0) {
            return false;
        }
        return allowedValues.some((entry: any) => {
            if (entry === null || entry === undefined) {
                return false;
            }
            if (typeof entry === 'string') {
                return entry.trim().length === 0;
            }
            if (typeof entry === 'object' && 'label' in entry) {
                const label = entry.label;
                return typeof label === 'string' && label.trim().length === 0;
            }
            return false;
        });
    }

    // Detects whether this field's own allowedValues contains a real DB-backed reference option
    // (a positive id) whose extracted display value (via allowedValueKey) is blank - for example
    // RingTerminal's Plating id=41 -> plating: "". This is intentionally narrower than
    // objectDescriptionAllowsBlankValue: it only recognizes id-backed reference entries (never plain
    // label-list entries), and is used to let ValidationService bypass required/pattern checks solely
    // when a row has actually selected that specific real reference - never for other fields.
    private objectDescriptionAllowsIdBackedBlankValue(objectDescription: any): boolean {
        const allowedValues = objectDescription?.allowedValues;
        const allowedValueKey = objectDescription?.allowedValueKey;
        if (!Array.isArray(allowedValues) || allowedValues.length === 0 || !allowedValueKey) {
            return false;
        }
        return allowedValues.some((entry: any) => {
            if (!entry || typeof entry !== 'object') {
                return false;
            }
            const hasRealId = typeof entry.id === 'number' && entry.id > 0;
            if (!hasRealId) {
                return false;
            }
            const comparable = entry[allowedValueKey];
            return typeof comparable === 'string' && comparable.trim().length === 0;
        });
    }

    private getMaxAllowedDisplayLength(objectDescription: any): number {
        const allowedValues = objectDescription?.allowedValues;
        if (!Array.isArray(allowedValues) || allowedValues.length === 0) {
            return 0;
        }
        const allowedValueKey = objectDescription?.allowedValueKey;
        return allowedValues.reduce((maxLength: number, option: any) =>
            Math.max(maxLength, this.getAllowedDisplayLength(option, allowedValueKey)), 0);
    }

    private getAllowedDisplayLength(option: any, allowedValueKey?: string): number {
        if (option == null) {
            return 0;
        }
        if (typeof option === 'string') {
            return option.length;
        }
        if (typeof option === 'object') {
            if (allowedValueKey && option[allowedValueKey] != null) {
                return String(option[allowedValueKey]).length;
            }
            if ('label' in option && option.label != null) {
                return String(option.label).length;
            }
        }
        return 0;
    }

    /**
     * Discards pending edits. The grid keeps the filter model that produced the page it is showing: clearing it would
     * both hide the criteria from the user and ask the server for a different set of rows.
     */
    protected reset(): void {
        this.clearEditState();
    }

    private getData(): void {
        if (!this.serverPaged()) {
            this.loadFullComponentList();
            return;
        }
        if (!this.editPermissionsResolved() || !this.componentColumnsReady) {
            return;
        }
        // Put the URL's criteria and the configured default filters into the grid before asking for a page, so the
        // first page already matches what the filter chips say. Applying the model itself must not trigger a second
        // request for the same rows.
        this.applyingServerGridState = true;
        try {
            this.applyInitialQueryParamsToActiveFiltersAndGrid();
        } finally {
            this.applyingServerGridState = false;
        }
        this.getServerPage(true);
    }

    /**
     * The client-mode (opted-out) counterpart to getServerPage(): the whole, unfiltered list for this catalog, held
     * in the browser and paged/filtered/sorted by AG Grid's own native row model rather than by another round trip
     * to the server. The URL's criteria and configured default filters are applied afterward as the grid's own
     * filter model, which AG Grid then matches against these rows itself - the same mechanism a user's own column
     * filter uses - rather than asking the server to narrow what is fetched.
     *
     * Fetched in FULL_LIST_FETCH_CHUNK_SIZE-row pages rather than one unbounded getObjects(component) call: a
     * catalog with tens of thousands of rows would otherwise pull its whole table over the wire, and hold it all in
     * a single response in memory, on every page load.
     */
    private loadFullComponentList(): void {
        const loadingFor = this.componentName;
        this.loading = true;
        this.fetchFullComponentListChunk(loadingFor, 0, []);
    }

    /** One bounded offset/limit page of loadFullComponentList(), recursing until a page comes back short of the
     * server-reported total, then handing the assembled rows to the grid. */
    private fetchFullComponentListChunk(loadingFor: string, offset: number, accumulated: any[]): void {
        const request: GridPageRequest = {offset, limit: EsciComponentGridComponent.FULL_LIST_FETCH_CHUNK_SIZE};
        (this.esciService.getObjects(this.componentName, buildGridPageParams(request)) as any).subscribe({
            next: (page: any) => {
                if (this.hasLeftComponent(loadingFor)) {
                    return;
                }
                const rows = page?.results ?? [];
                const combined = accumulated.concat(rows);
                const total = page?.total ?? combined.length;
                if (rows.length > 0 && combined.length < total) {
                    this.fetchFullComponentListChunk(loadingFor, offset + rows.length, combined);
                    return;
                }
                this.finishLoadingFullComponentList(combined);
            }, error: (err: any) => {
                if (this.hasLeftComponent(loadingFor)) {
                    return;
                }
                console.error(err);
                this.messageService.processError(err?.error?.message || err?.message || err);
                this.reset();
            }
        });
    }

    /** Applies the fully-assembled loadFullComponentList() rows to the grid, once every chunk has arrived. */
    private finishLoadingFullComponentList(rows: any[]): void {
        this.conditionRowsFromServer(rows);
        this.unfilteredRowData = rows;
        this.setRowData(rows);
        this.areRowsValid(this.rowData);
        this.setColumnsVisible();
        this.applyingServerGridState = true;
        try {
            this.applyInitialQueryParamsToActiveFiltersAndGrid();
        } finally {
            this.applyingServerGridState = false;
        }
        this.reset();
    }

    /**
     * Applies initial URL query params to Active Search Criteria and grid filter.
     * Also applies configured default filters when relevant columns exist.
     */
    private applyInitialQueryParamsToActiveFiltersAndGrid(): void {
        if (!this.objectDescription?.members) {
            return;
        }
        const memberNameToDisplayName = new Map<string, string>();
        for (const member of this.objectDescription.members) {
            if (member.memberName) {
                memberNameToDisplayName.set(member.memberName, member.displayName || member.memberName);
            }
        }
        const fallbackFilterMap = new Map<string, string[]>();
        const filterModel: any = {};
        for (const [paramKey, paramValue] of Object.entries(this.initialQueryParams || {})) {
            const displayName = memberNameToDisplayName.get(paramKey);
            if (!displayName) {
                continue;
            }
            const valueArray = Array.isArray(paramValue)
                ? paramValue.filter(v => v != null && v !== '')
                : (paramValue != null && paramValue !== '' ? [paramValue] : []);
            if (valueArray.length === 0) {
                continue;
            }
            fallbackFilterMap.set(displayName, valueArray);
            filterModel[paramKey] = EsciComponentGridComponent.buildFilterModelEntry(valueArray);
        }
        for (const [memberName, lockedValues] of Object.entries(this.readOnlyDefaultFiltersForPage())) {
            const displayName = memberNameToDisplayName.get(memberName);
            if (!displayName) {
                continue;
            }
            fallbackFilterMap.set(displayName, lockedValues);
            filterModel[memberName] = EsciComponentGridComponent.buildFilterModelEntry(lockedValues);
        }
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.setFilterModel(Object.keys(filterModel).length > 0 ? filterModel : null);
            this.updateActiveFilters();
            return;
        }
        // Grid API is not ready; keep a best-effort criteria summary until model can be applied.
        this.activeFiltersMap = fallbackFilterMap;
        this.activeFiltersArray = Array.from(fallbackFilterMap.entries()).map(([key, value]) => ({ key, value }));
    }

    private readOnlyDefaultFiltersForPage(): Record<string, string[]> {
        if (!this.isReadOnlyUser() || !this.objectDescription?.members) {
            return {};
        }
        return Object.fromEntries(Object.entries(this.defaultGridFilters())
            .map(([memberName, values]) => [
                memberName,
                (values || []).filter(value => value != null && value !== '')
            ] as [string, string[]])
            .filter(([memberName, values]) => values.length > 0 && !!this.findDescribedMember(memberName)));
    }

    private isReadOnlyLockedMember(memberName: string | null | undefined): boolean {
        return !!memberName && Object.prototype.hasOwnProperty.call(this.readOnlyDefaultFiltersForPage(), memberName);
    }

    isLockedFilter(displayName: string): boolean {
        return this.isReadOnlyLockedMember(this.getParamKeyForDisplayName(displayName));
    }

    removableFilterCount(): number {
        return this.activeFiltersArray.filter(filter => !this.isLockedFilter(filter.key)).length;
    }

    private isApprovedLockedRow(rowData: any): boolean {
        const status = String(CommonHelperService.getDeepValue(rowData, 'developmentStatus') || '')
            .trim()
            .toLowerCase();
        return status === EsciComponentGridComponent.APPROVED_DEVELOPMENT_STATUS;
    }

    /**
     * True when a row's developmentStatus is changing away from Approved back to an editable state
     * (Draft/Pending), the one transition that requires the user to supply a history reason
     * (see DevelopmentStatusReasonDialogComponent / Component.getUpdateFieldMessage on the backend).
     */
    private static isApprovedToEditableDowngrade(row: any): boolean {
        if (row?.state === 'new') {
            return false;
        }
        const originalStatus = String(
            CommonHelperService.getDeepValue(row?.originalRowData, 'developmentStatus') || ''
        ).trim().toLowerCase();
        const currentStatus = String(CommonHelperService.getDeepValue(row, 'developmentStatus') || '')
            .trim()
            .toLowerCase();
        return originalStatus === EsciComponentGridComponent.APPROVED_DEVELOPMENT_STATUS &&
            EsciComponentGridComponent.EDITABLE_DEVELOPMENT_STATUSES.includes(currentStatus);
    }

    /** Short, human-readable label for a row, used in the reason-required dialog's affected-rows list. */
    private static getRowDisplayLabel(row: any): string {
        const partNumber = CommonHelperService.getDeepValue(row, 'gmPartNumber');
        if (partNumber !== null && partNumber !== undefined && partNumber !== '') {
            return String(partNumber);
        }
        if (row?.id !== null && row?.id !== undefined && row.id !== -1) {
            return `#${row.id}`;
        }
        return 'New row';
    }

    /**
     * Gate before any save that may include a Development Status Approved -> Draft/Pending downgrade.
     * liveRows and saveCopies must be parallel arrays (same row, same index) - saveCopies are the
     * prepareRowForSave() clones that will actually be sent to the server. When one or more rows
     * qualify, prompts once for a single shared reason and applies it only to the qualifying save
     * copies (never to the live grid row model) before invoking onProceed. When none qualify,
     * onProceed runs immediately with no dialog. Cancelling the dialog simply skips onProceed, so the
     * save never happens.
     */
    private promptForDevelopmentStatusReasonIfNeeded(liveRows: any[], saveCopies: any[],
                                                     onProceed: () => void): void {
        const qualifyingIndexes = liveRows
            .map((_, index) => index)
            .filter(index => EsciComponentGridComponent.isApprovedToEditableDowngrade(liveRows[index]));
        if (qualifyingIndexes.length === 0) {
            onProceed();
            return;
        }
        this.ref = this.dialogService.open(DevelopmentStatusReasonDialogComponent, {
            header: 'Reason Required',
            data: {
                affectedRowLabels: qualifyingIndexes.map(index =>
                                                        EsciComponentGridComponent.getRowDisplayLabel(liveRows[index])),
                onConfirm: (reason: string) => {
                    qualifyingIndexes.forEach(index => {
                        saveCopies[index].updateMessage = reason;
                    });
                    onProceed();
                },
                onCancel: () => {
                    // The save is simply not performed - see saveAttemptAbandoned.
                    this.saveAttemptAbandoned = true;
                }
            }
        });
    }

    private static buildFilterModelEntry(values: string[]): any {
        return { values };
    }

    /**
     * Starts this grid's paging state from scratch, so nothing - page, page size, sort or filter - carries over from
     * the grid the user was looking at before.
     */
    private resetServerPagingState(): void {
        this.serverPageSize.set(this.paginationPageSize);
        this.serverPageIndex.set(0);
        this.serverTotalRows.set(0);
        this.serverSortBy = null;
        this.serverSortDir = 'asc';
        this.lastServerRequestSignature = '';
        this.lastAppliedFilterModel = null;
        this.lastAppliedSortState = [];
        this.advancedFilterCriteria = null;
        this.serverPageNeedsReconcile = false;
    }

    /**
     * Brings the page back in step with the table after a create or delete the server accepted.
     *
     * The count under the paginator and the rows on screen both came from one request, so a row added or removed
     * since leaves the two describing a table that no longer exists: the count is out by one, a delete leaves the
     * page a row short, and because pages are cut with OFFSET every row after the deleted one has shifted back a
     * slot - the row that was first on the next page would be skipped entirely. Only re-reading the page settles
     * all three. Client (opted-out) mode needs none of it: it holds the whole list, so AG Grid recounts itself.
     *
     * @param rowDelta how many rows the table gained or lost; 0 for an update, which changes neither the count nor
     *                 which rows belong on the page, and so only pays a re-read already owed
     */
    private reconcileServerPageAfterMutation(rowDelta: number): void {
        if (!this.serverPaged() || (rowDelta === 0 && !this.serverPageNeedsReconcile)) {
            return;
        }
        if (rowDelta !== 0) {
            // An estimate until the answer arrives, but the paginator has to stop claiming a row that has gone even
            // when the re-read below is held back.
            this.serverTotalRows.update(total => Math.max(0, total + rowDelta));
            // Deleting the last rows of the last page leaves the index past the end of the list, which would read a
            // page that no longer exists; step back to the last one the remaining rows reach.
            if (this.serverPageIndex() > this.totalServerPages() - 1) {
                this.serverPageIndex.set(this.totalServerPages() - 1);
            }
        }
        this.serverPageNeedsReconcile = true;
        if (this.dataChanged) {
            return;
        }
        this.serverPageNeedsReconcile = false;
        // The request is unchanged - only the table behind it - so the read has to be forced past the signature
        // check that would otherwise take it for a repeat of the one already on screen.
        this.getServerPage(true);
    }

    /**
     * Loads the page the paging state currently describes. Unlike the unpaged path this keeps the grid's filters: they
     * are what produced these rows, and the server already applied them.
     *
     * @param force true to load even when the request matches what is already on screen (initial load, Reset Changes)
     */
    private getServerPage(force = false): void {
        const request: GridPageRequest = {
            offset: this.serverPageIndex() * this.serverPageSize(),
            limit: this.serverPageSize(),
            sortBy: this.serverSortBy ?? undefined,
            sort: this.serverSortDir,
            criteria: this.collectServerCriteria(),
            partialKeys: this.partialMatchKeys()
        };
        const signature = JSON.stringify(request);
        if (!force && signature === this.lastServerRequestSignature) {
            return;
        }
        this.lastServerRequestSignature = signature;
        this.rememberAppliedGridState();
        const loadingFor = this.componentName;
        const requestedAt = this.componentDataGeneration;
        const requestId = ++this.serverPageRequestGeneration;
        this.loading = true;
        this.showServerRefetchOverlay();
        this.esciService.getObjectsPaged(this.componentName, request).subscribe({
            next: (page: any) => {
                // A response the grid has moved on from leaves the overlay alone: a newer request owns it now, and
                // clearing it here would uncover rows that are themselves about to be replaced.
                if (this.isStaleResponse(loadingFor, requestedAt, requestId)) {
                    return;
                }
                this.hideServerRefetchOverlay();
                const rows = page?.results ?? [];
                this.conditionRowsFromServer(rows);
                this.setRowData(rows);
                this.areRowsValid(this.rowData);
                this.serverTotalRows.set(page?.total ?? rows.length);
                this.setColumnsVisible();
                // The rows arriving is what makes their references knowable. Without this a page reached any way
                // other than refreshGrid - the first load, and every page after it - showed its similar components
                // as bare ids or as nothing, because the rows they name are almost never on the page naming them.
                this.updateMultiSelectIdsColumnOptions();
                this.reset();
            }, error: (err: any) => {
                if (this.isStaleResponse(loadingFor, requestedAt, requestId)) {
                    return;
                }
                this.hideServerRefetchOverlay();
                console.error(err);
                this.messageService.processError(err?.error?.message || err?.message || err);
                this.reset();
            }
        });
    }

    /**
     * Whether an answer that has arrived is still one the grid wants. Several page, sort and filter requests can be in
     * flight at once and the network decides which returns first, so only the newest may set the rows and the count: an
     * abandoned one landing last would leave the grid showing a page its paginator no longer points at. The component
     * is checked too, since the signature describes the request but does not name what it was asked of.
     *
     * <p>Identified by the request id rather than by the signature it was built from. A forced read - the initial
     * load, the post-save reconcile, Reset Changes - skips the signature check on purpose, so two requests can be
     * in flight agreeing on catalog, visit and signature alike; the older answer would then hide the newer
     * request's overlay and, landing second, leave its own rows on screen. Counting requests tells them apart, and
     * subsumes the signature comparison it replaces: any newer request has a higher id, whatever it asked for.
     */
    private isStaleResponse(requestedFor: string, requestedAt: number, requestId: number): boolean {
        return this.hasLeftComponentVisit(requestedFor, requestedAt)
               || requestId !== this.serverPageRequestGeneration;
    }

    /**
     * Whether the grid has moved on to another component since a request was issued. Ids are bare numbers, so rows or
     * options answered for the previous component would render beside a matching id of this one.
     */
    private hasLeftComponent(requestedFor: string): boolean {
        return requestedFor !== this.componentName;
    }

    /**
     * Whether the grid has moved on from the *visit* a request was issued during, which is what every asynchronous
     * answer here has to ask. The name alone cannot tell two visits apart: leaving a catalog and returning makes it
     * current again, so an answer the first visit abandoned is taken for the second's and writes behind the caches
     * applyComponentSwitch cleared. loadComponentData counts the visits.
     */
    private hasLeftComponentVisit(requestedFor: string, requestedAt: number): boolean {
        return this.hasLeftComponent(requestedFor) || requestedAt !== this.componentDataGeneration;
    }

    /**
     * The filters to apply server-side: the URL query params the page was opened with, plus the grid's own filter
     * model. Members the query cannot match (synthetic fields the UI computes) are skipped, since the server has
     * nothing to apply them to.
     */
    private collectServerCriteria(): Record<string, string[]> {
        const criteria: Record<string, string[]> = {};
        for (const [key, value] of Object.entries(this.initialQueryParams || {})) {
            const values = EsciComponentGridComponent.toFilterValues(value);
            if (values.length > 0) {
                this.warnIfNotServerFilterable(key, 'URL filter');
                criteria[key] = values;
            }
        }
        const filterModel = this.isGridApiAlive(this.gridApi) ? (this.gridApi.getFilterModel() || {}) : {};
        for (const [memberName, model] of Object.entries<any>(filterModel)) {
            if (!this.isServerFilterableMember(memberName)) {
                continue;
            }
            const values = EsciComponentGridComponent.toFilterValues(model?.values ?? model?.filter ?? model);
            if (values.length > 0) {
                criteria[this.serverQueryName(memberName)] = values;
            }
        }
        for (const [memberName, values] of Object.entries(this.advancedFilterCriteria?.criteria || {})) {
            if (values.length === 0) {
                continue;
            }
            this.warnIfNotServerFilterable(memberName, 'Advanced Filter');
            criteria[this.serverQueryName(memberName)] = values;
        }
        // AG Grid can defer creating Angular filter components after setFilterModel(). Locked defaults are therefore
        // also request criteria in their own right, rather than depending on getFilterModel() being current.
        for (const [memberName, values] of this.readOnlyServerDefaultFilters()) {
            criteria[this.serverQueryName(memberName)] = values;
        }
        return criteria;
    }

    /**
     * The criteria whose values are to be looked for <em>within</em> what a row stores rather than compared to it
     * whole. A multi-select column keeps every value a row holds in one delimited string, so a selection compared
     * whole matches only the rows holding exactly that one value - which is why the grid's own column filters and the
     * filters a link arrives with are named here too, not just the Advanced Filter's.
     * <p>
     * Keyed the same way {@link collectServerCriteria} keys the criteria themselves, or the server would not find the
     * criterion these names refer to.
     */
    private partialMatchKeys(): string[] {
        const keys = new Set(this.advancedFilterCriteria?.partialKeys ?? []);
        for (const key of Object.keys(this.initialQueryParams || {})) {
            if (this.isMultiSelectMember(key)) {
                keys.add(key);
            }
        }
        const filterModel = this.isGridApiAlive(this.gridApi) ? (this.gridApi.getFilterModel() || {}) : {};
        for (const memberName of Object.keys(filterModel)) {
            if (this.isServerFilterableMember(memberName) && this.isMultiSelectMember(memberName)) {
                keys.add(this.serverQueryName(memberName));
            }
        }
        for (const [memberName] of this.readOnlyServerDefaultFilters()) {
            if (this.isMultiSelectMember(memberName)) {
                keys.add(this.serverQueryName(memberName));
            }
        }
        return [...keys];
    }

    private readOnlyServerDefaultFilters(): Array<[string, string[]]> {
        return Object.entries(this.readOnlyDefaultFiltersForPage())
            .filter(([memberName]) => this.isServerFilterableMember(memberName));
    }

    /**
     * Whether a member holds several values at once. This is the same test the column's own filter applies when it
     * decides to compare the values of a row one by one rather than its whole cell, so the two stay in step; the
     * backend describes such a member as a dropdown taking more than one value.
     */
    private isMultiSelectMember(memberName: string): boolean {
        const member = this.findDescribedMember(memberName);
        return !!member && (member.type === 'multi_select' || (member.maxSelectionCount ?? 1) !== 1);
    }

    /**
     * The name to ask the server to filter or sort by, which is not always the name the row field carries. A dropdown
     * fed by rows of another table offers the values shown there while this table holds only their ids, so a request
     * naming the member would match and order by numbers the user never saw. The description publishes the path to
     * the displayed value for exactly those members; every other one is queried as itself.
     *
     * Values arriving in the URL are left as they are: those name a field a link chose, not a column this grid
     * described, and may already be the id the previous page had in hand.
     */
    private serverQueryName(memberName: string): string {
        return this.findDescribedMember(memberName)?.serverQueryMember || memberName;
    }

    /**
     * Reports a filter on a member the description says the server cannot match, which would leave the grid showing
     * rows the filter chip says are excluded. It is still sent: a service with a search of its own may know the member
     * even where the generic query cannot reach it, so dropping it here would quietly widen the list instead. A name
     * the description does not carry at all is left alone - the request may name something other than a column.
     */
    private warnIfNotServerFilterable(memberName: string, source: string): void {
        if (this.findDescribedMember(memberName) && !this.isServerFilterableMember(memberName)) {
            console.warn(`${source} field '${memberName}' cannot be filtered by the server`);
        }
    }

    private static toFilterValues(value: any): string[] {
        const raw = Array.isArray(value) ? value : [value];
        return raw.filter(v => v !== null && v !== undefined && v !== '' && typeof v !== 'object')
                  .map(v => String(v));
    }

    private isServerFilterableMember(memberName: string): boolean {
        return !!this.findDescribedMember(memberName)?.serverFilterable;
    }

    private isServerSortableMember(memberName: string): boolean {
        return !!this.findDescribedMember(memberName)?.serverSortable;
    }

    /**
     * The description of the column the grid keyed a filter or sort by. Columns showing a value from a related
     * object are described in a child description under their dotted member name, so the search has to descend.
     */
    private findDescribedMember(memberName: string): MemberDescription | undefined {
        const search = (desc: ObjectDescription | undefined): MemberDescription | undefined => {
            if (!desc) {
                return undefined;
            }
            const found = desc.members?.find(member => member.memberName === memberName);
            if (found) {
                return found;
            }
            for (const child of desc.children || []) {
                const inChild = search(child.value);
                if (inChild) {
                    return inChild;
                }
            }
            return undefined;
        };
        return search(this.objectDescription);
    }

    /** Moves to the page (or page size) chosen in the paginator. A new page size starts again at the first page. */
    onServerPageChange(event: { first?: number, rows?: number }): void {
        const requestedSize = event?.rows ?? this.serverPageSize();
        const sizeChanged = requestedSize !== this.serverPageSize();
        const requestedIndex = sizeChanged ? 0 : Math.floor((event?.first ?? 0) / Math.max(requestedSize, 1));
        this.confirmLeavingUnsavedEdits(() => {
            this.serverPageSize.set(requestedSize);
            this.serverPageIndex.set(requestedIndex);
            this.getServerPage();
        });
    }

    goToFirstServerPage(): void {
        this.goToServerPage(0);
    }

    goToPreviousServerPage(): void {
        this.goToServerPage(this.serverPageIndex() - 1);
    }

    goToNextServerPage(): void {
        this.goToServerPage(this.serverPageIndex() + 1);
    }

    goToLastServerPage(): void {
        this.goToServerPage(this.totalServerPages() - 1);
    }

    /**
     * Loads a page of the current list by its index. A page the list does not have is not asked for at all: a button
     * that leads nowhere is disabled, but it can still be reached by keyboard, and asking would otherwise prompt about
     * unsaved edits to end up on the page already shown.
     */
    private goToServerPage(pageIndex: number): void {
        if (pageIndex < 0 || pageIndex > this.totalServerPages() - 1 || pageIndex === this.serverPageIndex()) {
            return;
        }
        this.onServerPageChange({first: pageIndex * this.serverPageSize(), rows: this.serverPageSize()});
    }

    /** Shows the chosen number of rows a page, from the first page: the page the user was on may no longer exist. */
    changeServerPageSize(pageSize: number): void {
        this.pageSizePickerOpen.set(false);
        if (pageSize === this.serverPageSize()) {
            return;
        }
        this.onServerPageChange({first: 0, rows: pageSize});
    }

    togglePageSizePicker(): void {
        if (!this.pageSizePickerOpen()) {
            this.pageSizePickerHighlight.set(Math.max(0, this.paginationPageSizeSelector
                                                            .indexOf(this.serverPageSize())));
        }
        this.pageSizePickerOpen.update(open => !open);
    }

    closePageSizePicker(): void {
        this.pageSizePickerOpen.set(false);
    }

    /**
     * Works the page-size list from the keyboard the way a list of options is expected to work: it opens, the arrows
     * move through the sizes without loading any of them, and only Enter or Space asks for the highlighted one.
     */
    onPageSizePickerKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape' || event.key === 'Tab') {
            this.closePageSizePicker();
            return;
        }
        const step = (event.key === 'ArrowDown') ? 1 : (event.key === 'ArrowUp') ? -1 : 0;
        const chooses = (event.key === 'Enter' || event.key === ' ');
        if (step === 0 && !chooses) {
            return;
        }
        event.preventDefault();
        if (!this.pageSizePickerOpen()) {
            this.togglePageSizePicker();
        } else if (chooses) {
            this.changeServerPageSize(this.paginationPageSizeSelector[this.pageSizePickerHighlight()]);
        } else {
            const last = this.paginationPageSizeSelector.length - 1;
            this.pageSizePickerHighlight.update(at => Math.min(Math.max(at + step, 0), last));
        }
    }

    /** Closes the list when focus leaves the picker altogether, as clicking elsewhere on the page does. */
    onPageSizePickerFocusOut(event: FocusEvent): void {
        const movedTo = event.relatedTarget as HTMLElement | null;
        if (!movedTo?.closest('.ag-paging-page-size')) {
            this.closePageSizePicker();
        }
    }

    /**
     * Sorts in the database rather than within the loaded page, starting again at the first page. In client mode
     * this is a no-op: AG Grid already re-ordered the whole list itself, over the same row objects, so there is
     * nothing left to fetch and no unsaved edit is at risk.
     */
    onSortChanged(): void {
        if (!this.serverPaged() || this.applyingServerGridState || !this.isGridApiAlive(this.gridApi)) {
            return;
        }
        const sorted = (this.gridApi.getColumnState() || []).filter((state: any) => !!state.sort)
                           .sort((a: any, b: any) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
        const primary = sorted[0];
        const sortBy = (primary && this.isServerSortableMember(primary.colId))
                       ? this.serverQueryName(primary.colId) : null;
        const sortDir = primary?.sort === 'desc' ? 'desc' : 'asc';
        this.confirmLeavingUnsavedEdits(() => {
            this.serverSortBy = sortBy;
            this.serverSortDir = sortDir;
            this.serverPageIndex.set(0);
            this.getServerPage();
        }, () => this.restoreAppliedGridState());
    }

    /**
     * Re-queries after a filter change, coalescing the several events ag-grid emits while a filter menu is being used.
     */
    private scheduleServerPageRefresh(): void {
        if (this.serverFilterDebounceTimer) {
            clearTimeout(this.serverFilterDebounceTimer);
        }
        this.serverFilterDebounceTimer = setTimeout(() => {
            this.serverFilterDebounceTimer = null;
            this.confirmLeavingUnsavedEdits(() => {
                this.serverPageIndex.set(0);
                this.getServerPage();
            }, () => this.restoreAppliedGridState());
        }, EsciComponentGridComponent.SERVER_FILTER_DEBOUNCE_MS);
    }

    /**
     * Runs `onProceed` unless rows have unsaved edits, in which case the user chooses first: saving keeps them,
     * discarding drops them, and cancelling leaves the grid exactly as it is. Under server paging any of these
     * actions replaces every row on screen, so edits would otherwise disappear without a word.
     */
    private confirmLeavingUnsavedEdits(onProceed: () => void, onCancel?: () => void): void {
        if (!this.dataChanged) {
            onProceed();
            return;
        }
        this.confirmationService.confirm({
            key: 'serverPagingConfirmation',
            header: 'Unsaved Changes',
            message: 'This page has unsaved changes, and loading different rows will replace them. ' +
                     'Save them first, discard them, or stay on this page?',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Save changes',
            rejectLabel: 'Discard changes',
            accept: () => {
                this.saveAttemptAbandoned = false;
                this.saveAll();
                this.runAfterSaveSettles(onProceed);
            },
            reject: (type: ConfirmEventType) => {
                // Only the Discard button drops edits; dismissing the dialog (escape, mask, close icon) means "stay".
                if (type === ConfirmEventType.REJECT) {
                    onProceed();
                    return;
                }
                onCancel?.();
            }
        });
    }

    /**
     * Waits for the save started by the confirm dialog to finish before loading different rows. The save path reports
     * completion only through the component's own `saving`/`dataChanged` flags (each row is posted separately, with no
     * single completion callback), so this watches those flags and gives up rather than waiting forever.
     */
    private runAfterSaveSettles(onProceed: () => void, attempt = 0): void {
        if (!this.saving && !this.dataChanged) {
            onProceed();
            return;
        }
        if (this.saveAttemptAbandoned) {
            this.saveAttemptAbandoned = false;
            this.messageService.processError(
                'Save was not completed - fix any invalid rows (or the reason dialog was cancelled) and try again. ' +
                'Your edits are still on this page.');
            return;
        }
        if (attempt >= EsciComponentGridComponent.MAX_SAVE_SETTLE_ATTEMPTS) {
            // The save is still outstanding; the rows stay put and the user can retry the page change.
            this.messageService.processError(
                'Still waiting for the save to finish. Your edits are still on this page - please try again.');
            return;
        }
        setTimeout(() => this.runAfterSaveSettles(onProceed, attempt + 1),
                   EsciComponentGridComponent.SAVE_SETTLE_POLL_MS);
    }

    private rememberAppliedGridState(): void {
        if (!this.isGridApiAlive(this.gridApi)) {
            return;
        }
        this.lastAppliedFilterModel = this.gridApi.getFilterModel();
        this.lastAppliedSortState = (this.gridApi.getColumnState() || [])
            .filter((state: any) => !!state.sort)
            .map((state: any) => ({colId: state.colId, sort: state.sort}));
    }

    /** Puts the grid's filters and sort back to what the displayed page matches, after the user cancels a change. */
    private restoreAppliedGridState(): void {
        if (!this.isGridApiAlive(this.gridApi)) {
            return;
        }
        this.applyingServerGridState = true;
        try {
            this.gridApi.setFilterModel(this.lastAppliedFilterModel);
            this.gridApi.applyColumnState({state: this.lastAppliedSortState, defaultState: {sort: null}});
        } finally {
            this.applyingServerGridState = false;
        }
    }

    private conditionRowsFromServer(rowData: any[]) {
        let rowNumber = 0;
        for (const row of rowData) {
            row.rowNumber   = rowNumber++;
            row.fieldRules  = this.fieldRules;
            row.changes     = new Map();
            row.state       = 'saved';
            for (const field of Object.keys(row)) {
                this.conditionFromServer(row, field);
            }
            row.originalRowData = structuredClone(row);
        }
        this.markConglomerationOutOfSyncRows(rowData);
    }

    /**
     * For each row, if a conglomeration field value does not match the value computed from
     * its constituent fields, mark that row so we show server values and yellow highlight
     * (never overwrite the conglomeration field with the computed value).
     */
    private markConglomerationOutOfSyncRows(rowData: any[]): void {
        const members = this.objectDescription?.members;
        if (!members?.length) {
            return;
        }
        for (const row of rowData) {
            let outOfSyncSet: Set<string> | undefined;
            for (const member of members) {
                if (member.type !== 'conglomeration' || !member.fields) {
                    continue;
                }
                const conglomerationFieldName = member.memberName;
                const computed = buildConglomerationFromRow(row, member.fields);
                const serverVal = row[conglomerationFieldName];
                const serverStr = serverVal == null ? '' : String(serverVal).trim();
                if (computed !== serverStr) {
                    const perField = getOutOfSyncFieldsForConglomeration(row, conglomerationFieldName, member.fields);
                    if (!outOfSyncSet) {
                        outOfSyncSet = new Set<string>();
                    }
                    perField.forEach((f: string) => outOfSyncSet!.add(f));
                }
            }
            if (outOfSyncSet?.size) {
                row._conglomerationOutOfSyncFields = outOfSyncSet;
                row._conglomerationOutOfSyncMessage = 'Conglomeration field is out of sync with constituent fields.';
            }
        }
    }

    /**
     * Clear out-of-sync state for one conglomeration on a row after user has edited
     * the conglomeration field (full parse), so the row is now in sync.
     */
    private clearConglomerationOutOfSyncForRow(
        row: any,
        conglomerationFieldName: string,
        conglomerationFields: string
    ): void {
        const set = row._conglomerationOutOfSyncFields as Set<string> | undefined;
        if (!set) {
            return;
        }
        const fieldNames = conglomerationFields.split(',').map((t: string) => t.trim()).filter((t: string) => t !== '-');
        fieldNames.forEach((f: string) => set.delete(f));
        set.delete(conglomerationFieldName);
        if (set.size === 0) {
            delete row._conglomerationOutOfSyncFields;
            delete row._conglomerationOutOfSyncMessage;
        }
    }

    /**
     * After editing one constituent (segment-only update), recompute which fields
     * are still out of sync for this conglomeration and update the row's set.
     */
    private recomputeConglomerationOutOfSyncForRow(
        row: any,
        conglomerationFieldName: string,
        conglomerationFields: string
    ): void {
        const fieldNames = conglomerationFields.split(',').map((t: string) => t.trim()).filter((t: string) => t !== '-');
        const toRemove = new Set<string>([conglomerationFieldName, ...fieldNames]);
        const set = row._conglomerationOutOfSyncFields as Set<string> | undefined;
        if (set) {
            toRemove.forEach((f: string) => set.delete(f));
        }
        const stillOutOfSync = getOutOfSyncFieldsForConglomeration(row, conglomerationFieldName, conglomerationFields);
        const merged = set ?? new Set<string>();
        stillOutOfSync.forEach((f: string) => merged.add(f));
        if (merged.size > 0) {
            row._conglomerationOutOfSyncFields = merged;
            row._conglomerationOutOfSyncMessage = 'Conglomeration field is out of sync with constituent fields.';
        } else {
            delete row._conglomerationOutOfSyncFields;
            delete row._conglomerationOutOfSyncMessage;
        }
    }

    private conditionFromServer(row: any, field: string) {
        let val = row[field];
        if (!val) {
            return;
        }
        // for each row, format all the entries in columns that have a cellEditorParams member
        // to replace every ";" with "; "
        const flatCols = this.flattenColDefs(this.cols);
        const colDef = flatCols.find(col => col.field === field);
        const columnType = colDef?.cellEditorParams?.memberType;
        if (columnType === 'multiSelectIdsDropdown') {
            if (Array.isArray(val)) {
                val = this.parseMultiSelectIdsValue(val);
            } else if (val === EsciComponentGridComponent.RESTRICTED_INT ||
                       val === String(EsciComponentGridComponent.RESTRICTED_INT)) {
                val = [];
            } else {
                val = this.parseMultiSelectIdsValue(val);
            }
        } else if (columnType == 'multiSelectDropdown') {
            if (typeof val === 'string') {
                // Replace every ";" with "; ", unless its already is followed by a " "
                val = val.replace(/;(?=[^ ])/g, '; ');
            }
        } else if (columnType === 'date') {
            if (typeof val === 'number') {
                // These come in as milliseconds since the epoch
                val = new Date(val);
            } else if (typeof val === 'string') {
                val = CommonHelperService.calculateDateWithOffset(val);
            }
        } else if (columnType === 'number' && typeof val === 'string') {
            const numVal = parseFloat(val);
            if (!isNaN(numVal)) {
                val = numVal;
            }
        }

        row[field] = val;
    }

    private conditionRowsForServer(rowData: any[]) {
        const flatCols = this.flattenColDefs(this.cols);
        for (const row of rowData) {
            delete row.originalRowData;
            delete row.rowNumber;
            delete row.changes;
            delete row.state;
            delete row.dataChanged; // TODO: remove this
            delete row.fieldRules;
            delete row._cellValidationErrors;
            delete row._conglomerationOutOfSyncFields;
            delete row._conglomerationOutOfSyncMessage;
            delete row.componentName;
            // Don't send PPCO data if user doesn't have PURCH_ADMIN permission
            if (!this.hasPurchAdminPermission()) {
                delete row.ppco;
            }
            // Same defense-in-depth for Tier-N Resilient: strip when the caller can't
            // edit it. The backend also preserves the DB value, but stripping here keeps
            // the wire payload honest and avoids confusing audit history.
            if (!this.hasTierNAdminPermission()) {
                delete row.tierNResilient;
                delete row.tierNResilientId;
            }
            // Keep conditioning top-level fields for backward compatibility.
            for (const field of Object.keys(row)) {
                this.conditionForServer(row, field);
            }
            // Also condition nested fields defined by column defs (e.g. connectorService.additionalPartType).
            for (const col of flatCols) {
                if (col.field && !col.field.includes('[]')) {
                    this.conditionForServer(row, col.field);
                }
            }
        }
    }

    private conditionForServer(row: any, field: string) {
        let val = CommonHelperService.getDeepValue(row, field);
        // Use cached flattened columns for better performance
        const flatCols = this.flattenColDefs(this.cols);
        const column = flatCols.find(col => col.field === field);
        const columnType = column?.cellEditorParams?.memberType;
        const valueFormatString = column?.cellEditorParams?.valueFormatString;
        const allowedValueKey = column?.cellEditorParams?.allowedValueKey;
        const isListManagerStructure = column?.cellEditorParams?.isListManagerStructure;
        const availableOptions = column?.cellEditorParams?.availableOptions;

        // For object dropdowns, treat blank string as cleared selection and send null.
        if (columnType === 'objectDropdown' && val === '') {
            CommonHelperService.setDeepValue(row, field, null);
            return null;
        }

        // Early return for remaining falsy values (null, undefined, 0, false, etc.)
        if (!val) {
            return val;
        }

        // If this column has a valueFormatString, remove formattedLabel from object values
        // (formattedLabel is added by the dropdown editor for display purposes only)
        // Check for object type explicitly (not null, not array) to handle edge cases
        if (valueFormatString && typeof val === 'object' && !Array.isArray(val)) {
            if ('formattedLabel' in val) {
                delete val.formattedLabel;
            }
        }

        if (columnType === 'multiSelectIdsDropdown') {
            val = this.parseMultiSelectIdsValue(val);
        // for each row, format all the entries in columns that have a cellEditorParams member
        // to replace every ";" with "; "
        } else if (columnType === 'multiSelectDropdown') {
            // to replace every "; " with ";"
            if (typeof val === 'string') {
                val = val.replace(/; ?/g, ';');
            }
        } else if (columnType === 'objectDropdown' && allowedValueKey) {
            if (typeof val === 'object') {
                const hasId = Object.prototype.hasOwnProperty.call(val, 'id') && val.id != null;
                if (isListManagerStructure) {
                    val = val[allowedValueKey] ?? null;
                } else if (hasId) {
                    // Keep DB-backed reference objects (e.g. StrategyWave) as objects for backend deserialization.
                } else {
                    // Non-reference helper objects (without id) are serialized by their key value.
                    val = val[allowedValueKey] ?? null;
                }
            } else if (!isListManagerStructure && typeof val === 'string' &&
                       Array.isArray(availableOptions)) {
                const matchingOption = availableOptions.find((option: any) =>
                    option && typeof option === 'object' && option[allowedValueKey] === val
                );
                if (matchingOption) {
                    val = matchingOption;
                }
            }
        } else if (columnType === 'date') {
            if (column.type === 'varchar') {
                // Since the column is a string, we need to format the date as yyyy-MM-dd:
                val = CommonHelperService.formatDate(val);
            } else {
                // Since the column is not a string, we need to return the value to the server as milliseconds since epoch
                if (typeof val === 'string') {
                    val = new Date(val);
                }
                if (typeof val?.getTime === 'function') {
                    val = val.getTime();
                }
            }
        }
        CommonHelperService.setDeepValue(row, field, val);
    }

    private parseMultiSelectIdsValue(value: any): number[] {
        if (value == null || value === '') {
            return [];
        }
        const asList = Array.isArray(value)
            ? value
            : String(value).split(/[;,]/).map(v => v.trim());
        const parsed: number[] = [];
        for (const entry of asList) {
            const numeric = Number(entry);
            if (!Number.isFinite(numeric)) {
                continue;
            }
            const id = Math.trunc(numeric);
            if (id < 1 ||
                id === EsciComponentGridComponent.RESTRICTED_INT ||
                parsed.includes(id)) {
                continue;
            }
            parsed.push(id);
        }
        return parsed;
    }

    /**
     * The member names shown beside an id in this component's similar-components picker, taken from the `fields` of
     * the member's own declaration. Every component declares a different set - Ring Terminal a GM part number and a
     * supplier part number, Terminal a part name and a plating and no supplier part number at all - so naming any of
     * them here would make the picker work for one catalog and offer bare ids for the rest.
     *
     * `id` leads every declaration and is what the picker stores, so it is not also asked for as a label.
     */
    private getIdOptionLabelFields(): string[] {
        const member = (this.objectDescription?.members || [])
            .find((candidate: MemberDescription) =>
                      this.getMultiSelectIdsFieldNames().includes(candidate.memberName));
        return this.parseDisplayTypeFields(member?.fields).filter(field => field !== 'id');
    }

    /**
     * Asks the server for the components matching what the user typed. Whatever comes back is also remembered, so a
     * row pointed at one of them can render its labels without a second request.
     *
     * The grid is deliberately not refreshed here. Search answers only the picker, and rebuilding the column
     * definitions on each one re-rendered every row behind an open dropdown for a result none of them use.
     */
    private searchComponentIdOptions(searchText: string): Observable<ComponentIdOption[]> {
        const labelFields = this.getIdOptionLabelFields();
        if (!this.componentName || labelFields.length === 0) {
            return of([]);
        }
        const searchingFor = this.componentName;
        const searchingAt = this.componentDataGeneration;
        return this.esciService.getObjectIdOptions(searchingFor, labelFields, searchText,
                                                   EsciComponentGridComponent.ID_OPTION_SEARCH_LIMIT)
                   .pipe(map(options => {
                       if (this.hasLeftComponentVisit(searchingFor, searchingAt)) {
                           return [];
                       }
                       this.rememberComponentIdOptions(options);
                       return options;
                   }), shareReplay({bufferSize: 1, refCount: false}));
    }

    /**
     * Keeps every option a search has turned up, so the ones already chosen stay resolvable as the user types on.
     * Searches overlap heavily - "ring" and "ring t" return largely the same components - so an id already held is
     * overwritten rather than appended. Appending grew the set by a full page per keystroke and made every later
     * read of it slower than the one before, which is what made typing in the picker drag.
     *
     * An unsaved row's -1 and the restricted sentinel are not components anyone can point at, so they are dropped
     * here rather than filtered out on each read.
     */
    private rememberComponentIdOptions(options: ComponentIdOption[]): void {
        for (const option of options || []) {
            const id = Number(option?.id);
            if (!Number.isFinite(id) || id < 1 || id === EsciComponentGridComponent.RESTRICTED_INT) {
                continue;
            }
            this.componentIdOptions.set(Math.trunc(id), {id: Math.trunc(id), labels: option?.labels ?? {}});
        }
    }

    private getCurrentComponentIdOptions(): string[] {
        return this.getCurrentComponentSelectOptions().map(option => String(option.id));
    }

    /**
     * What the picker can offer, each id once and in id order. A server-paged grid holds one page, so it offers what
     * its searches have turned up. A client-mode grid holds the whole catalog, so it offers those rows.
     */
    private getCurrentComponentSelectOptions(): ComponentIdOption[] {
        if (!this.serverPaged()) {
            return this.getHeldComponentIdOptions();
        }
        return Array.from(this.componentIdOptions.values()).sort((a, b) => a.id - b.id);
    }

    /**
     * Every row a client-mode grid holds, as a picker option labelled with the declared fields. Read from the
     * unfiltered list rather than the rows on screen, so narrowing the grid with a filter does not narrow the picker.
     * An object-valued label (Terminal's plating) is left blank, as the server search drops it, so a catalog's
     * captions read the same in either paging mode.
     */
    private getHeldComponentIdOptions(): ComponentIdOption[] {
        const labelFields = this.getIdOptionLabelFields();
        const rows = this.unfilteredRowData?.length ? this.unfilteredRowData : (this.rowData || []);
        const optionsById = new Map<number, ComponentIdOption>();
        for (const row of rows) {
            const id = Number((row as any)?.id);
            if (!Number.isFinite(id) || id < 1 || id === EsciComponentGridComponent.RESTRICTED_INT) {
                continue;
            }
            const labels: Record<string, string> = {};
            for (const field of labelFields) {
                const value = CommonHelperService.getDeepValue(row, field);
                labels[field] = (value === null || value === undefined || typeof value === 'object') ? ''
                                                                                                   : String(value);
            }
            optionsById.set(Math.trunc(id), {id: Math.trunc(id), labels});
        }
        return Array.from(optionsById.values()).sort((a, b) => a.id - b.id);
    }

    /**
     * Fetches the rows the given id fields point at but the page does not hold, so their display columns render as
     * values rather than as nothing. Every missing id goes in one request, and one already fetched is not asked for
     * again.
     */
    private resolveOffPageIdLabels(rows: ValidationModel[], idFieldNames: string[]): void {
        if (!this.componentName || idFieldNames.length === 0) {
            return;
        }
        const onPage = new Set(this.rowIdsOf(rows));
        const wanted = this.offPageIdsReferencedBy(rows, idFieldNames, onPage);
        if (wanted.length === 0) {
            return;
        }
        wanted.forEach(id => this.offPageIdsRequested.add(id));
        const loadingFor = this.componentName;
        const loadingAt = this.componentDataGeneration;
        // A large page whose rows each name several components can reference more ids than one request should carry,
        // so the set goes out in bounded chunks. Each answers on its own; a chunk that fails only frees its own ids.
        for (const chunk of EsciComponentGridComponent.chunkIds(wanted)) {
            this.esciService.getObjectsByIds(loadingFor, chunk).subscribe({
                next: page => {
                    if (this.hasLeftComponentVisit(loadingFor, loadingAt)) {
                        return;
                    }
                    this.cacheOffPageRows(page?.results);
                    this.gridApi?.refreshCells({force: true});
                },
                error: err => {
                    if (this.hasLeftComponentVisit(loadingFor, loadingAt)) {
                        return;
                    }
                    // Allow another attempt: without these rows the ids show with no values beside them.
                    chunk.forEach(id => this.offPageIdsRequested.delete(id));
                    console.error('Could not load the rows referenced by ids for', loadingFor, err);
                }
            });
        }
    }

    /** Splits an id set into requests small enough to send, preserving order so the chunks reassemble the set. */
    private static chunkIds(ids: number[]): number[][] {
        const chunks: number[][] = [];
        for (let at = 0; at < ids.length; at += EsciComponentGridComponent.OFF_PAGE_ID_CHUNK_SIZE) {
            chunks.push(ids.slice(at, at + EsciComponentGridComponent.OFF_PAGE_ID_CHUNK_SIZE));
        }
        return chunks;
    }

    /** The id of a row, or null where it has none to read - an unsaved row, or one the server answered without one. */
    private rowIdOf(row: ValidationModel): number | null {
        const id = Number((row as any)?.id);
        return Number.isFinite(id) ? Math.trunc(id) : null;
    }

    /** The ids of the rows given, skipping any without one. */
    private rowIdsOf(rows: ValidationModel[]): number[] {
        return (rows || []).map(row => this.rowIdOf(row))
                           .filter((id): id is number => id !== null);
    }

    /**
     * The ids these rows point at that nothing can put a value beside yet: not on the page, not already fetched, and
     * not already asked for. Sorted, so the same set of ids is always requested the same way.
     */
    private offPageIdsReferencedBy(rows: ValidationModel[], idFieldNames: string[], onPage: Set<number>): number[] {
        const missing = new Set<number>();
        for (const id of this.referencedIdsAcross(rows, idFieldNames)) {
            if (!onPage.has(id) && !this.offPageRowsById.has(id) && !this.offPageIdsRequested.has(id)) {
                missing.add(id);
            }
        }
        return Array.from(missing).sort((a, b) => a - b);
    }

    /** Every id the given rows name across the given id fields, each id once. */
    private referencedIdsAcross(rows: ValidationModel[], idFieldNames: string[]): Set<number> {
        const referenced = new Set<number>();
        for (const row of rows || []) {
            this.referencedIdsOf(row, idFieldNames).forEach(id => referenced.add(id));
        }
        return referenced;
    }

    /** Every id one row names, across all of its id fields - a field can hold several. */
    private referencedIdsOf(row: ValidationModel, idFieldNames: string[]): number[] {
        return idFieldNames.flatMap(
            idFieldName => this.parseMultiSelectIdsValue(CommonHelperService.getDeepValue(row, idFieldName)));
    }

    /** Keeps the fetched rows by id, so a later page holding the same reference does not ask for them again. */
    private cacheOffPageRows(rows: ValidationModel[]): void {
        for (const row of rows || []) {
            const id = this.rowIdOf(row);
            if (id !== null) {
                this.offPageRowsById.set(id, row);
            }
        }
    }

    /** The id fields whose values name other rows of this same component. */
    private getMultiSelectIdsFieldNames(): string[] {
        return (this.objectDescription?.members || [])
            .filter((member: MemberDescription) =>
                        member.type === 'multi_select_ids' ||
                        member.memberName?.toLowerCase().replace(/[^a-z]/g, '') === 'similarcomponentids')
            .map((member: MemberDescription) => member.memberName);
    }

    private updateMultiSelectIdsColumnOptions(): void {
        const idFieldNames = this.getMultiSelectIdsFieldNames();
        if (idFieldNames.length > 0) {
            this.resolveOffPageIdLabels(this.rowData, idFieldNames);
        }
        const idOptions = this.getCurrentComponentIdOptions();
        const allCols = this.getColsFromColDefOrColGroupDef(this.cols || []);
        for (const col of allCols) {
            const cellEditorParams = col.cellEditorParams as any;
            if (cellEditorParams?.memberType !== 'multiSelectIdsDropdown') {
                continue;
            }
            cellEditorParams.availableOptions = [...idOptions];
            cellEditorParams.allowedValues = [...idOptions];
            col.cellRendererParams = {
                ...(col.cellRendererParams || {}),
                allowedValues: [...idOptions],
                isListManagerStructure: false
            };
        }
    }

    // This method is called when the user clicks the save or save all button in the grid.
    // It makes a copy of the row and sets the onSuccess callback to update the row in the grid.
    prepareRowForSave(row: any): any {
        const copy = structuredClone(row);
        copy.onSuccess = (newId) => {
            const n = typeof newId === 'string' ? parseInt(newId, 10) : Number(newId);
            if (!Number.isFinite(n)) {
                console.error('Invalid id from save response:', newId);
                return;
            }
            const currentId = row['id'];
            if (currentId != null && currentId !== -1 && Number(currentId) !== n) {
                console.error(`Wrong id returned! expected ${currentId} but got ${newId}`);
                return;
            }
            // Read before applyRowSaveSuccess puts the server's id on the row, which is what makes it a saved row.
            const wasCreate = currentId == null || currentId === -1;
            // Defer row mutation (notably id -1 -> server id) to avoid NG0100 when save callback
            // resolves while Angular is still checking the current change detection cycle.
            setTimeout(() => this.applyRowSaveSuccess(row, n, wasCreate), 0);
        };
        copy.onError = (err?: any) => {
            this.markRowRejectedAsDuplicate(row, err);
            // Detail-dialog saves intentionally avoid refreshing before the request completes so row-node identity
            // remains stable. Refresh only after a failed request so the unchanged "changed" state is visible again.
            this.refreshGrid(this.rowData);
            this.dataChanged = this.rowData?.some(candidate => candidate?.state !== 'saved') ?? false;
        };
        return copy;
    }

    /**
     * Puts a duplicate the server refused onto the row it refused, on the fields that made it one.
     *
     * The grid's own check only compares the rows it holds, which under server paging is one page, so a duplicate
     * further down the list is only ever reported by the server. Which fields to mark comes from the same
     * `uniqueFieldSetName` rules that check uses, so the two agree on what made the row a duplicate.
     */
    private markRowRejectedAsDuplicate(row: any, err?: any): void {
        if (err?.status !== EsciComponentGridComponent.DUPLICATE_REFUSED_STATUS) {
            return;
        }
        const message = (typeof err.error === 'string' && err.error.trim())
            ? err.error.trim()
            : 'This row duplicates one that already exists. Change the value and try again.';
        const uniqueFields = (row?.fieldRules || [])
            .filter((rule: FieldRule) => !!rule.uniqueFieldSetName)
            .map((rule: FieldRule) => rule.fieldName);
        if (uniqueFields.length === 0) {
            return;
        }
        const rowErrors = new Map<string, string>(row._cellValidationErrors ?? []);
        for (const fieldName of uniqueFields) {
            rowErrors.set(fieldName, message);
        }
        row._cellValidationErrors = rowErrors;
        row.state = 'changed';
        // Blocks a second attempt at the same values; the next save revalidates and clears it.
        this.duplicate = true;
    }

    /**
     * @param wasCreate whether this save added a row to the table rather than changing one already in it, which is
     *                  what decides if the page's row count has moved
     */
    private applyRowSaveSuccess(row: any, newId: number, wasCreate = false): void {
        row['id'] = newId;
        delete row.originalRowData;
        row.state = 'saved';
        row.changes?.clear();
        row.originalRowData = structuredClone(row);
        row.originalRowData.changes?.clear();
        // The catalog a picker searches now holds a row it did not, or one under a different label. The searches are
        // live, so only the labels read for ids off the page have to be dropped.
        this.offPageRowsById.delete(newId);
        this.offPageIdsRequested.delete(newId);

        // Keep button states in sync immediately after server confirms save.
        this.dataChanged = this.rowData?.some(r => r?.state !== 'saved') ?? false;
        if (this.hasPPCO) {
            this.updatePPCODataChanged();
        }
        if (this.hasTierNResilient) {
            this.updateTierNDataChanged();
        }

        if (this.gridApi && !this.gridApi.isDestroyed()) {
            let targetNode: any = null;
            this.gridApi.forEachNode((node) => {
                if (targetNode) {
                    return;
                }
                const nodeData = node?.data;
                if (!nodeData) {
                    return;
                }
                if (nodeData === row) {
                    targetNode = node;
                    return;
                }
                if (row['id'] !== null && row['id'] !== undefined && row['id'] !== -1 && nodeData['id'] === row['id']) {
                    targetNode = node;
                    return;
                }
                if (row.rowNumber !== null && row.rowNumber !== undefined && nodeData.rowNumber === row.rowNumber) {
                    targetNode = node;
                }
            });

            if (targetNode) {
                targetNode.setData(row);
                this.gridApi.refreshCells({rowNodes: [targetNode], force: true});
                this.gridApi.redrawRows({rowNodes: [targetNode]});
            } else {
                // Fallback in case row node is not currently rendered (e.g., paging/filtering transitions).
                this.gridApi.refreshCells({force: true});
                this.gridApi.redrawRows();
            }
        }
        this.syncRowDataSignal();
        this.reconcileServerPageAfterMutation(wasCreate ? 1 : 0);
    }

    private resolveLiveRow(row: any): any {
        if (!row || !this.rowData || this.rowData.length === 0) {
            return row;
        }
        const byRef = this.rowData.indexOf(row);
        if (byRef >= 0) {
            return this.rowData[byRef];
        }
        if (row['id'] !== null && row['id'] !== undefined && row['id'] !== -1) {
            const byId = this.rowData.find(r => r['id'] === row['id']);
            if (byId) {
                return byId;
            }
        }
        if (row.rowNumber !== null && row.rowNumber !== undefined) {
            const byRowNumber = this.rowData.find(r => r.rowNumber === row.rowNumber);
            if (byRowNumber) {
                return byRowNumber;
            }
        }
        return row;
    }

    onSaveClick(params: any): void {
        const liveRow = this.resolveLiveRow(params.data);
        if (liveRow.state === 'saved') {
            return;
        }
        // Row-level Actions Save should validate only the selected row.
        if (!this.areRowsValid([liveRow])) {
            this.refreshGrid(this.rowData);
            return;
        }
        const row = this.prepareRowForSave(liveRow);
        if (liveRow.state === 'deleted') {
            this.deleteRows([row['id']]);
            return;
        }
        this.promptForDevelopmentStatusReasonIfNeeded([liveRow], [row], () => {
            this.conditionRowsForServer([row]);
            this.esciService.saveObjects(this, this.componentName, [row]);
            // Avoid an immediate full-grid refresh here. The onSuccess callback from prepareRowForSave
            // performs the refresh when the row save completes, which keeps row-save interactions responsive.
        });
    }

    onViewDetailsClick(params: any, isCreate: boolean): void {
        this.ref = this.dialogService.open(ViewDetailDialogComponent, {
            header: isCreate ? ('New ' + this.objectDescription.displayName) :
                               (this.objectDescription.displayName + ' ' + params.data.id + ' Details'),
            styleClass: 'custom-wide-dialog',
            data: {
                rowData: params.data,
                cols: this.cols,
                fieldRules: this.fieldRules,
                component: this.componentName,
                hasAdminPermission: !!this.adminPermission(),
                hasPurchAdminPermission: !!this.hasPurchAdminPermission(),
                hasTierNAdminPermission: !!this.hasTierNAdminPermission(),
                isCreate: isCreate,
                onApply: (newData: any) => {
                    // Apply already merged editors into rowData; validate (pattern, duplicates, etc.) then refresh.
                    this.areRowsValid(this.rowData);
                    this.refreshGrid(this.rowData);
                },
                onSave: () => {
                    // Modal already merged fields into the live row. Do not refreshGrid here — it runs before the
                    // async save finishes and can break row-node identity so onSuccess never updates the row (copy flow).
                    const liveRow = this.resolveLiveRow(params.data);
                    if (!this.areRowsValid([liveRow])) {
                        this.refreshGrid(this.rowData);
                        return;
                    }
                    const row = this.prepareRowForSave(liveRow);
                    this.promptForDevelopmentStatusReasonIfNeeded([liveRow], [row], () => {
                        this.conditionRowsForServer([row]);
                        this.esciService.saveObjects(this, this.componentName, [row]);
                    });
                },
                onSavePPCOOnly: () => {
                    const liveRow = this.resolveLiveRow(params.data);
                    const row = this.prepareRowForSave(liveRow);
                    this.conditionRowsForServer([row]);
                    this.esciService.savePPCOOnly(this, this.componentName, [row]);
                },
                onRevertPPCOOnly: () => {
                    const liveRow = this.resolveLiveRow(params.data);
                    if (!liveRow?.changes) {
                        return;
                    }
                    for (const field of liveRow.changes.keys()) {
                        if (field.startsWith('ppco.')) {
                            const originalValue = CommonHelperService.getDeepValue(liveRow.originalRowData, field);
                            CommonHelperService.setDeepValue(liveRow, field, originalValue);
                            liveRow.changes.delete(field);
                        }
                    }
                    if (liveRow.changes.size === 0 && liveRow.state === 'changed') {
                        liveRow.state = 'saved';
                    }
                    this.updatePPCODataChanged();
                    this.refreshGrid(this.rowData);
                    this.messageService.processSuccess('PPCO changes reverted.');
                },
                onSaveTierNResilientOnly: () => {
                    const liveRow = this.resolveLiveRow(params.data);
                    const row = this.prepareRowForSave(liveRow);
                    this.conditionRowsForServer([row]);
                    this.esciService.saveTierNResilientOnly(this, this.componentName, [row]);
                },
                onRevertTierNResilientOnly: () => {
                    const liveRow = this.resolveLiveRow(params.data);
                    if (!liveRow?.changes) {
                        return;
                    }
                    for (const field of liveRow.changes.keys()) {
                        if (field === 'tierNResilient' || field.startsWith('tierNResilient.')) {
                            const originalValue = CommonHelperService.getDeepValue(liveRow.originalRowData, field);
                            CommonHelperService.setDeepValue(liveRow, field, originalValue);
                            liveRow.changes.delete(field);
                        }
                    }
                    if (liveRow.changes.size === 0 && liveRow.state === 'changed') {
                        liveRow.state = 'saved';
                    }
                    this.updateTierNDataChanged();
                    this.refreshGrid(this.rowData);
                    this.messageService.processSuccess('Tier-N Resilient changes reverted.');
                },
                onCancel: () => {
                    if (isCreate) {
                        this.onDeleteClick(params, this.rowData, (updatedData) => this.refreshGrid(updatedData));
                    }
                },
            }
        });
    }

    onRevertClick(params: any): void {
        const row = params.data;
        if (row.state === 'new') {
            return this.onDeleteClick(params, this.rowData, (updatedData) => this.refreshGrid(updatedData));
        }
        const flatCols = this.flattenColDefs(this.cols);
        for (const col of flatCols) {
            if (!col.field) {
                continue;
            }
            const originalValue = CommonHelperService.getDeepValue(row.originalRowData, col.field);
            CommonHelperService.setDeepValue(row, col.field, originalValue);
        }
        // Clear per-cell change markers immediately so reverted rows/cells lose highlight right away.
        row.changes?.clear();
        params.data.state = 'saved';
        if (params.node && this.gridApi && !this.gridApi.isDestroyed()) {
            this.gridApi.refreshCells({rowNodes: [params.node], force: true});
            this.gridApi.redrawRows({rowNodes: [params.node]});
        }
        // Keep button state in sync immediately (avoid waiting for deferred refresh cycle).
        this.dataChanged = this.rowData?.some(r => r?.state !== 'saved') ?? false;
        if (this.hasPPCO) {
            this.updatePPCODataChanged();
        }
        if (this.hasTierNResilient) {
            this.updateTierNDataChanged();
        }
        // update the grid
        this.refreshGrid(this.rowData);
    }

    onCopyClick(params): void {
        let index = this.rowData.indexOf(params.data);
        if (index === -1) {
            index = this.rowData.findIndex(row => row['id'] === params.data.id);
        }
        if (index >= 0) {
            const newRow = structuredClone(params.data);
            newRow.id = -1;
            newRow.state = 'new';
            newRow.fieldRules = this.fieldRules;
            if ('developmentStatus' in newRow) {
                newRow.developmentStatus = 'Draft';
            }
            if ('gmApprovalStatus' in newRow) {
                newRow.gmApprovalStatus = 'Under Development';
            }
            this.rowData.splice(index + 1, 0, newRow);
            this.refreshGrid(this.rowData);

            newRow.partImageFileIds = {};
            delete newRow.imageFiles;
            delete newRow.partImageUrl;
            delete newRow.partImageFileId;
            delete newRow.attachmentUrl;
            delete newRow.attachmentName;
            delete newRow.attachmentFileId;
            delete newRow.validationReportFileId;
            delete newRow.validationReportFile;

            if (this.hasPPCO && newRow.ppco) {
                newRow.ppco = { ...newRow.ppco };
                delete newRow.ppco.id;
            }

            this.onViewDetailsClick({ data: newRow }, true);
        }
    }

    /** Returns the Advanced Filter field-config builder registered for componentName, if any. */
    private getAdvancedFilterFieldsBuilder(componentName: string): AdvancedFilterFieldsBuilder | undefined {
        return ADVANCED_FILTER_FIELD_BUILDERS[componentName];
    }

    advancedFilter(): void {
        const fieldsBuilder = this.getAdvancedFilterFieldsBuilder(this.componentName);
        if (!fieldsBuilder) {
            return;
        }
        // open the advanced search dialog
        this.ref = this.dialogService.open(AdvancedFilterModalComponent, {
            header: this.ADVANCED_FILTER,
            styleClass: 'advanced-filter-dialog',
            data: {
                fields: fieldsBuilder(this.objectDescription?.members ?? []),
                onSearch: (criteria: AdvancedFilterCriteria, filterValue: string[]) =>
                    this.applyAdvancedFilterCriteria(criteria, filterValue),
                onCancelSearch: () => {
                    this.removeAdvancedFilter();
                    // Update the grid with the search results
                    this.refreshGrid(this.rowData);
                }
            }
        });
    }

    /**
     * Applies what the Advanced Filter dialog asked for. Under server paging the filter has to be applied by the
     * query, since the rows it selects are mostly not in the browser; a grid holding the whole list narrows the
     * rows it already has instead.
     */
    private applyAdvancedFilterCriteria(advanced: AdvancedFilterCriteria, filterValue: string[]): void {
        this.advancedFilterCriteria = advanced;
        this.activeFiltersMap.set(this.ADVANCED_FILTER, filterValue);
        this.activeFiltersArray = Array.from(this.activeFiltersMap.entries())
                                       .map(([key, value]) => ({ key, value }));
        if (!this.serverPaged()) {
            this.refreshGrid(this.filterRowsByAdvancedCriteria(advanced));
            return;
        }
        this.serverPageIndex.set(0);
        this.getServerPage();
    }

    /**
     * Client-mode Advanced Filter: narrows the whole list already in the browser rather than asking the server for
     * a different set of rows. A field named in partialKeys is matched as a case-insensitive substring, the same
     * looser match the search page gives a typed-in value; every other field must equal one of the chosen values.
     */
    private filterRowsByAdvancedCriteria(advanced: AdvancedFilterCriteria): ValidationModel[] {
        const entries = Object.entries(advanced?.criteria || {}).filter(([, values]) => (values || []).length > 0);
        if (entries.length === 0) {
            return this.unfilteredRowData;
        }
        const partialKeys = new Set(advanced?.partialKeys || []);
        return this.unfilteredRowData.filter(
            row => entries.every(([memberName, values]) =>
                this.rowMatchesCriterion(row, memberName, values, partialKeys.has(memberName))));
    }

    /**
     * Whether one row satisfies one Advanced Filter criterion: any of the chosen values against any of the values the
     * row holds for that member, since a member can hold several. A partial criterion is satisfied by a value found
     * anywhere inside one of them, an exact one only by a value that is the whole of it.
     */
    private rowMatchesCriterion(row: ValidationModel, memberName: string, values: string[],
                                partial: boolean): boolean {
        const rowValue = CommonHelperService.getDeepValue(row, memberName);
        const rowValues = (Array.isArray(rowValue) ? rowValue : [rowValue])
            .map(v => String(v ?? '').toLowerCase());
        return values.some(value => partial
            ? rowValues.some(rv => rv.includes(String(value).toLowerCase()))
            : rowValues.includes(String(value).toLowerCase()));
    }

    updateActiveFilters(): void {
        super.updateActiveFilters();
        const filterModel = this.gridApi?.getFilterModel();

        const newFilterMap = new Map<string, string[]>();
        for (const colId in filterModel || []) {
            const filter = filterModel[colId];

            const col = this.gridApi.getColumnDef(colId);
            const colName = col?.headerName || colId

            // Handle Set Filter (values is an array)
            if (Array.isArray(filter)) {
                if (filter.length) {
                    newFilterMap.set(colName, filter);
                }
            } else if (typeof filter === 'string' || typeof filter === 'number') {
                if (filter !== '') {
                    newFilterMap.set(colName, [filter + '']);
                }
            } else if (Array.isArray(filter.values) ||
                typeof filter.filter === 'string' || typeof filter.filter === 'number') {
                if (filter.values.length) {
                    newFilterMap.set(colName, filter.values);
                }
            }
        }
        // preserve the Advanced Filter, if it exists.
        const advancedFilter = this.activeFiltersMap.get(this.ADVANCED_FILTER);
        if (advancedFilter) {
            newFilterMap.set(this.ADVANCED_FILTER, advancedFilter);
        }
        this.activeFiltersMap = newFilterMap;
        this.activeFiltersArray = Array.from(newFilterMap.entries())
                                       .map(([key, value]) => ({ key, value }));
    }

    saveAll() {
        if (!this.areRowsValid(this.rowData)) {
            this.refreshGrid(this.rowData);
            // No save was started, and none will be until the invalid rows are fixed - see saveAttemptAbandoned.
            this.saveAttemptAbandoned = true;
            return;
        }
        const deletedRows = this.rowData.filter(row => row.state === 'deleted');
        const deletedRowsIds = deletedRows.map(row => row['id'])
                                          .filter(id => id !== null && id !== undefined && id !== -1);
        if (deletedRows.length > 0) {
            // Optimistically remove deleted rows from the grid so UI reflects user action immediately.
            this.setRowData(this.rowData.filter(row => row.state !== 'deleted'));
            this.refreshGrid(this.rowData);
        }

        const liveModifiedRows = this.rowData.filter(row => row.state === 'new' || row.state === 'changed');
        const modifiedRows = liveModifiedRows.map(row => this.prepareRowForSave(row));
        if (modifiedRows.length === 0 && deletedRowsIds.length === 0) {
            this.saving = false;
            return;
        }
        this.promptForDevelopmentStatusReasonIfNeeded(liveModifiedRows, modifiedRows, () => {
            this.conditionRowsForServer(modifiedRows);
            this.esciService.saveObjects(this, this.componentName, modifiedRows);
        });
        this.deleteRows(deletedRowsIds);
    }

    /**
     * Save only PPCO changes for PURCH_ADMIN-only users.
     * This saves PPCO data without modifying the main component fields.
     */
    savePPCOChanges() {
        // Get rows that have PPCO changes (check if any ppco.* field has changed)
        const rowsWithPPCOChanges = this.rowData.filter(row => {
            if (!row.changes || row.changes.size === 0) {
                return false;
            }
            // Check if any of the changed fields start with 'ppco.'
            for (const field of row.changes.keys()) {
                if (field.startsWith('ppco.')) {
                    return true;
                }
            }
            return false;
        }).map(row => this.prepareRowForSave(row));

        if (rowsWithPPCOChanges.length === 0) {
            this.messageService.processError('No PPCO changes to save.');
            return;
        }

        this.conditionRowsForServer(rowsWithPPCOChanges);
        this.esciService.savePPCOOnly(this, this.componentName, rowsWithPPCOChanges);
        this.refreshGrid(this.rowData);
    }

    /**
     * Revert only PPCO changes for PURCH_ADMIN-only users.
     * This reverts PPCO field changes without affecting other component fields.
     */
    revertPPCOChanges() {
        for (const row of this.rowData) {
            if (!row.originalRowData || !row.changes) {
                continue;
            }
            // Revert only PPCO fields
            for (const field of row.changes.keys()) {
                if (field.startsWith('ppco.')) {
                    const originalValue = CommonHelperService.getDeepValue(row.originalRowData, field);
                    CommonHelperService.setDeepValue(row, field, originalValue);
                    row.changes.delete(field);
                }
            }
            // Update row state
            if (row.changes.size === 0 && row.state === 'changed') {
                row.state = 'saved';
            }
        }
        this.updatePPCODataChanged();
        this.refreshGrid(this.rowData);
        this.messageService.processSuccess('PPCO changes reverted.');
    }

    /**
     * Updates the ppcoDataChanged flag based on whether any rows have PPCO field changes.
     */
    private updatePPCODataChanged() {
        this.ppcoDataChanged = this.rowData?.some(row => {
            if (!row.changes || row.changes.size === 0) {
                return false;
            }
            for (const field of row.changes.keys()) {
                if (field.startsWith('ppco.')) {
                    return true;
                }
            }
            return false;
        }) ?? false;
    }

    /**
     * Mirror of {@link savePPCOChanges} for COMPONENT_TIER_N_ADMIN-only users. Sends only
     * rows whose pending edits include a {@code tierNResilient.*} field through the
     * dedicated /saveTierNResilientOnly endpoint, leaving all other component fields untouched.
     */
    saveTierNChanges() {
        const rowsWithChanges = this.rowData.filter(row => {
            if (!row.changes || row.changes.size === 0) {
                return false;
            }
            for (const field of row.changes.keys()) {
                // tierNResilientId on the backend is @JsonIgnore, so only tierNResilient
                // (the object) round-trips. Track changes only on the object/sub-fields.
                if (field === 'tierNResilient' || field.startsWith('tierNResilient.')) {
                    return true;
                }
            }
            return false;
        }).map(row => this.prepareRowForSave(row));

        if (rowsWithChanges.length === 0) {
            this.messageService.processError('No Tier-N Resilient changes to save.');
            return;
        }

        this.conditionRowsForServer(rowsWithChanges);
        this.esciService.saveTierNResilientOnly(this, this.componentName, rowsWithChanges);
        this.refreshGrid(this.rowData);
    }

    /**
     * Mirror of {@link revertPPCOChanges} for the Tier-N field. Reverts pending
     * tierNResilient changes back to the original values fetched from the DB.
     */
    revertTierNChanges() {
        for (const row of this.rowData) {
            if (!row.originalRowData || !row.changes) {
                continue;
            }
            for (const field of row.changes.keys()) {
                if (field === 'tierNResilient' || field.startsWith('tierNResilient.')) {
                    const originalValue = CommonHelperService.getDeepValue(row.originalRowData, field);
                    CommonHelperService.setDeepValue(row, field, originalValue);
                    row.changes.delete(field);
                }
            }
            if (row.changes.size === 0 && row.state === 'changed') {
                row.state = 'saved';
            }
        }
        this.updateTierNDataChanged();
        this.refreshGrid(this.rowData);
        this.messageService.processSuccess('Tier-N Resilient changes reverted.');
    }

    /**
     * Tracks whether any pending row edits affect the tierNResilient field. Drives
     * enable/disable state of the "Save Tier-N Only" action.
     */
    private updateTierNDataChanged() {
        this.tierNDataChanged = this.rowData?.some(row => {
            if (!row.changes || row.changes.size === 0) {
                return false;
            }
            for (const field of row.changes.keys()) {
                if (field === 'tierNResilient' || field.startsWith('tierNResilient.')) {
                    return true;
                }
            }
            return false;
        }) ?? false;
    }

    /**
     * Override refreshGrid to also update ppcoDataChanged for PURCH_ADMIN-only users.
     */
    override refreshGrid(rows: ValidationModel[], node?: any, onCompleteCallback?: () => void): void {
        super.refreshGrid(rows, node, () => {
            this.updateMultiSelectIdsColumnOptions();
            this.updateCollectionFieldChangedState(this.rowData);
            this.updateDerivedMultiSelectIdsChangedState(this.rowData);
            // Update PPCO data changed status after the grid refresh
            if (this.hasPPCO) {
                this.updatePPCODataChanged();
            }
            if (this.hasTierNResilient) {
                this.updateTierNDataChanged();
            }
            if (onCompleteCallback) {
                onCompleteCallback();
            }
        });
    }

    private updateCollectionFieldChangedState(rows: ValidationModel[]): void {
        const collectionCols = this.getColsFromColDefOrColGroupDef(this.cols || [])
            .filter(col => !!col.field && col.field.includes('[]'));
        if (collectionCols.length === 0) {
            return;
        }

        for (const row of rows || []) {
            if (!row || row.state === 'new' || row.state === 'deleted' || !row.originalRowData) {
                continue;
            }
            row.changes = row.changes || new Map<string, string>();

            for (const col of collectionCols) {
                const field = col.field as string;
                const newValuesRaw = this.getNestedArray(row, field);
                const oldValuesRaw = this.getNestedArray(row.originalRowData, field);
                const formatter = col.valueFormatter instanceof Function ? col.valueFormatter : null;
                const newValues = formatter ? newValuesRaw.map(v => formatter({ value: v } as any)) : newValuesRaw;
                const oldValues = formatter ? oldValuesRaw.map(v => formatter({ value: v } as any)) : oldValuesRaw;

                if (JSON.stringify(newValues) !== JSON.stringify(oldValues)) {
                    row.changes.set(field, 'Original Value: ' + oldValues.join('; '));
                } else {
                    row.changes.delete(field);
                }
            }

            row.state = row.changes.size > 0 ? 'changed' : 'saved';
        }

        this.dataChanged = rows?.some(r => r?.state !== 'saved') ?? false;
    }

    private updateDerivedMultiSelectIdsChangedState(rows: ValidationModel[]): void {
        const members = this.objectDescription?.members || [];
        const derivedMultiSelectIdsFields = members
            .filter((member: MemberDescription) => member.type === 'multi_select_ids' && !!member.fields)
            .map((member: MemberDescription) => member.memberName);
        if (derivedMultiSelectIdsFields.length === 0) {
            return;
        }

        for (const row of rows || []) {
            if (!row || row.state === 'new' || row.state === 'deleted' || !row.originalRowData) {
                continue;
            }
            row.changes = row.changes || new Map<string, string>();
            for (const fieldName of derivedMultiSelectIdsFields) {
                const currentIds = this.parseMultiSelectIdsValue(CommonHelperService.getDeepValue(row, fieldName));
                const originalIds = this.parseMultiSelectIdsValue(CommonHelperService.getDeepValue(row.originalRowData,
                                                                                                   fieldName));
                if (JSON.stringify(currentIds) !== JSON.stringify(originalIds)) {
                    row.changes.set(fieldName, 'Original Value: ' + originalIds.join('; '));
                } else {
                    row.changes.delete(fieldName);
                }
            }
            row.state = row.changes.size > 0 ? 'changed' : 'saved';
        }
        this.dataChanged = rows?.some(r => r?.state !== 'saved') ?? false;
    }

    private deleteRows(deletedRowsIds: number[]) {
        if (deletedRowsIds && deletedRowsIds.length > 0) {
            this.esciService.deleteObjectsByIds(this, this.componentName, deletedRowsIds, () => {
                deletedRowsIds.forEach(rowId => {
                    const index = this.rowData.findIndex(r => r['id'] === rowId);
                    if (index >= 0) {
                        this.rowData.splice(index, 1);
                    }
                });
                this.refreshGrid(this.rowData);
                // Counted from the ids the server accepted, not from the rows spliced here: saveAll takes them off
                // the grid optimistically before this callback runs, so by now there may be nothing left to splice.
                this.reconcileServerPageAfterMutation(-deletedRowsIds.length);
            });
        }
    }

    exportReport() {
        if (!this.serverPaged()) {
            // The whole list is already in the browser; export exactly what the grid is currently showing (its own
            // filter/sort applied) rather than asking the server for a report.
            const fileNamePrefix = this.componentName + 'Component-';
            const rows: ValidationModel[] = [];
            this.gridApi.forEachNodeAfterFilterAndSort(node => rows.push(node.data));
            this.excelService.exportGridRowsAsExcelFile(
                rows, this.gridApi.getColumnDefs() || [], fileNamePrefix, this.componentName);
            return;
        }
        // Fetch bounded pages so one response never materializes the whole catalog as JSON. The explicit cap also
        // bounds the rows retained while ExcelJS expands child collections and builds the workbook in the browser.
        const exportFor = this.componentName;
        const exportGeneration = ++this.exportGeneration;
        const isCurrentExport = () => exportGeneration === this.exportGeneration &&
            exportFor === this.componentName && !this.destroyed();
        const request: GridPageRequest = {
            offset: 0,
            limit: EsciComponentGridComponent.EXPORT_PAGE_SIZE,
            sortBy: this.serverSortBy ?? undefined,
            sort: this.serverSortDir,
            criteria: this.collectServerCriteria(),
            partialKeys: this.partialMatchKeys()
        };
        this.exportCancellation.next();
        this.exporting = true;
        this.fetchBoundedExportRows(exportFor, request).pipe(
            switchMap(rows => this.preloadExportLookupRows(exportFor, rows)),
            takeUntil(this.exportCancellation)
        ).subscribe({
            next: ({rows, exportRowsById}) => {
                if (!isCurrentExport()) {
                    if (exportGeneration === this.exportGeneration) {
                        this.exporting = false;
                    }
                    return;
                }
                this.exportRowsById = exportRowsById;
                const exportPromise = this.excelService.exportGridRowsAsExcelFile(
                    rows,
                    this.gridApi.getColumnDefs() || [],
                    exportFor + 'Component-',
                    exportFor,
                    isCurrentExport);
                void Promise.resolve(exportPromise).catch((err: any) => {
                    if (isCurrentExport()) {
                        console.error(err);
                        this.messageService.processError(err?.error?.message || err?.message || err);
                    }
                }).finally(() => {
                    if (this.exportRowsById === exportRowsById) {
                        this.exportRowsById = null;
                    }
                    if (exportGeneration === this.exportGeneration) {
                        this.exporting = false;
                    }
                });
            }, error: (err: any) => {
                if (isCurrentExport()) {
                    console.error(err);
                    this.exporting = false;
                    this.messageService.processError(err?.error?.message || err?.message || err);
                } else if (exportGeneration === this.exportGeneration) {
                    this.exporting = false;
                }
            }
        });
    }

    private fetchBoundedExportRows(componentName: string, request: GridPageRequest): Observable<ValidationModel[]> {
        type ExportPage = { page: { results: ValidationModel[]; total: number }; offset: number };
        const fetchPage = (offset: number): Observable<ExportPage> =>
            this.esciService.getObjectsForExport(componentName, {...request, offset}).pipe(
                map(page => ({page, offset}))
            );

        return fetchPage(0).pipe(
            expand(({page, offset}) => {
                const total = Number(page?.total ?? 0);
                if (total > EsciComponentGridComponent.MAX_CLIENT_EXPORT_ROWS) {
                    const formattedTotal = new Intl.NumberFormat('en-US').format(total);
                    const formattedLimit = new Intl.NumberFormat('en-US')
                        .format(EsciComponentGridComponent.MAX_CLIENT_EXPORT_ROWS);
                    return throwError(() => new Error(
                        `This export has ${formattedTotal} rows. Narrow the filters to ${formattedLimit} rows or fewer ` +
                        'and try again.'));
                }
                const pageRows = page?.results || [];
                const nextOffset = offset + pageRows.length;
                if (nextOffset >= total) {
                    return EMPTY;
                }
                if (pageRows.length === 0) {
                    return throwError(() => new Error('The export stopped because the server returned an incomplete page.'));
                }
                return fetchPage(nextOffset);
            }),
            reduce((rows, {page}) => {
                rows.push(...(page?.results || []));
                return rows;
            }, [] as ValidationModel[])
        );
    }

    private preloadExportLookupRows(componentName: string, rows: ValidationModel[]):
        Observable<{ rows: ValidationModel[]; exportRowsById: Map<number, ValidationModel> }> {
        const exportRowsById = new Map<number, ValidationModel>();
        for (const row of rows) {
            const id = this.rowIdOf(row);
            if (id !== null) {
                exportRowsById.set(id, row);
            }
        }

        const referencedIds = this.referencedIdsAcross(rows, this.getMultiSelectIdsFieldNames());
        for (const id of referencedIds) {
            const cached = this.offPageRowsById.get(id);
            if (!exportRowsById.has(id) && cached) {
                exportRowsById.set(id, cached);
            }
        }
        const missingIds = Array.from(referencedIds)
            .filter(id => !exportRowsById.has(id))
            .sort((a, b) => a - b);
        if (missingIds.length === 0) {
            return of({rows, exportRowsById});
        }

        // An export covers far more rows than a page does, so the ids they reference between them are asked for in
        // the same bounded chunks, and the export waits for all of them before it writes a workbook.
        //
        // A few chunks are in flight at a time rather than all of them. A 10,000-row export whose rows each name
        // several components divides into hundreds of chunks, and starting those at once spends the browser's
        // connection pool and the backend's on one workbook - a queue that takes longer is worth an export that
        // finishes.
        return from(EsciComponentGridComponent.chunkIds(missingIds)).pipe(
            mergeMap(chunk => this.esciService.getObjectsByIds(componentName, chunk),
                     EsciComponentGridComponent.EXPORT_LOOKUP_CONCURRENCY),
            reduce((resolved, page) => {
                for (const lookupRow of page?.results || []) {
                    const id = this.rowIdOf(lookupRow);
                    if (id !== null) {
                        resolved.set(id, lookupRow);
                    }
                }
                return resolved;
            }, exportRowsById),
            map(() => ({rows, exportRowsById}))
        );
    }

    handleFileAndDownloadToBrowserWithName(data: any, fileName: string) {
        const blob = new Blob([data], { type: 'application/octet-stream;charset=utf-8' });
        CommonHelperService.downloadFile(blob, fileName);
    }

    exportHistory() {
        this.esciService.getHistory(this.componentName).subscribe({
            next: (data: any) => {
                if (data.byteLength < 1) {
                    alert(`Exporting ${this.componentName} Catalog returned empty. Please contact an eSCI Admin.`);
                    return false;
                }

                // Date Time for reports in this format YYYYMMDD_HHMMSS
                const timestamp = CommonHelperService.getTimeStampForReport();
                const exportFileName = this.componentName + 'History-' + timestamp + '.xlsx';
                this.handleFileAndDownloadToBrowserWithName(data, exportFileName);
                return true;
            }, error: (err: any) => {
                console.error(err);
                this.messageService.processError(err?.error?.message || err?.message || err);
                this.reset();
            }
        });
    }

    override onUserDataLoaded() {
        this.sortColumns();
        this.setColumnsVisible();
        // User data can arrive after the grid/cols exist; re-apply saved layout before allowing persist.
        if (this.gridApi && this.cols?.length && this.componentName && this.persistentUserData) {
            this.columnPersistReady = false;
            this.sizeColumnsToFitIfInitialLoad();
            this.applySavedColumnWidthsToGrid();
            this.markColumnPersistenceReadyAfterLayout();
        }
    }

    // This method takes the users persisted preference data on search grid columns and applies it
    sortColumns() {
        if (!this.persistentUserData || !this.cols || !this.componentName) {
            return;
        }
        const key = this.componentName + 'CompColumnsShownInOrder';
        const { fieldOrder } = this.parseColumnsShownInOrder(key);
        if (!fieldOrder || fieldOrder.length === 0) {
            return;
        }
        this.inSetColumnVisibilityAndOrder = true;

        // Helper: extract all persisted-order keys under a col or group
        const extractAllFields = (col: ColDef | ColGroupDef): string[] => {
            if ('children' in col) {
                return col.children.flatMap(child => extractAllFields(child));
            } else {
                const key = this.getColumnOrderKeyFromColDef(col);
                if (key) {
                    return [key];
                }
            }
            return [];
        };

        // Helper: get the lowest index in fieldOrder for any leaf field in this group/col
        const getOrderIndex = (col: ColDef | ColGroupDef): number => {
            const fields = extractAllFields(col);
            const indexes = fields
                .map(f => fieldOrder.indexOf(f))
                .filter(idx => idx >= 0);
            return indexes.length ? Math.min(...indexes) : Number.MAX_SAFE_INTEGER;
        };

        const sortCols = (cols: (ColDef | ColGroupDef)[]): (ColDef | ColGroupDef)[] => {
            return cols
                .map(col => {
                    if ('children' in col) {
                        const group = col as ColGroupDef;
                        // Preserve child order only for explicit fixed-order groups.
                        // Other marryChildren groups (e.g. Connector Service) should honor persisted child order.
                        const preserveChildOrder = group.headerName === 'Specification Codes';
                        const children = preserveChildOrder ? group.children : sortCols(group.children);
                        return {
                            ...col,
                            children
                        };
                    }
                    return col;
                })
                .sort((a, b) => {
                    const aIdx = getOrderIndex(a);
                    const bIdx = getOrderIndex(b);
                    return aIdx - bIdx;
                });
        };
        // Keep fixed utility columns (e.g. Actions) and lockPosition columns out of persisted sorting.
        const isFixedUtilityCol = (col: ColDef | ColGroupDef): boolean =>
            col['lockPosition'] === true || (col as ColDef).colId === 'Actions';
        const sortableCols = this.cols.filter(col => !isFixedUtilityCol(col));
        const unsortableCols = this.cols.filter(col => isFixedUtilityCol(col));
        const sortableColsSorted = sortCols(sortableCols);
        this.replaceCols([...unsortableCols, ...sortableColsSorted]);
        this.invalidateFlatColsCache(); // Columns reordered, invalidate cache

        this.inSetColumnVisibilityAndOrder = false;
    }

    /**
     * Apply saved column order/widths from persistent user data to the current grid column model.
     */
    private applySavedColumnWidthsToGrid(): void {
        if (!this.gridApi || !this.cols?.length || !this.componentName) {
            return;
        }
        const key = this.componentName + 'CompColumnsShownInOrder';
        const { widthByField } = this.parseColumnsShownInOrder(key);
        const flatCols = this.flattenColDefs(this.cols);
        this.gridApi.applyColumnState({
            state: flatCols.map((col) => {
                const colId = col.colId || col.field;
                const state: {
                    colId: string;
                    sort: any;
                    width?: number;
                    pinned?: 'left' | 'right' | null;
                } = {
                    colId,
                    sort: col.sort || null,
                };
                if (col.pinned != null) {
                    state.pinned = col.pinned as 'left' | 'right' | null;
                }
                // Only set width when persisted (field:width). Otherwise leave current width — e.g. after
                // sizeColumnsToFitIfInitialLoad() so we do not replace fitted columns with colDef defaults.
                const key = this.getColumnOrderKeyFromColDef(col);
                if (key && widthByField.has(key)) {
                    state.width = widthByField.get(key);
                }
                return state;
            }),
            applyOrder: true,
        });
    }

    /**
     * Enables debounced column persist only after server user data exists and layout has been applied,
     * on the next macrotask so programmatic resize events from apply/sizeColumnsToFit are ignored.
     */
    private markColumnPersistenceReadyAfterLayout(): void {
        if (!this.persistentUserData) {
            return;
        }
        afterNextRender(() => {
            this.columnPersistReady = true;
        }, {injector: this.injector});
    }

    /**
     * Call sizeColumnsToFit once on initial load when the user has no saved column state,
     * so the grid fits the viewport without resetting user-resized widths on later edits.
     */
    private sizeColumnsToFitIfInitialLoad(): void {
        const key = this.componentName + 'CompColumnsShownInOrder';
        const saved = this.persistentUserData?.columnsMap?.[key];
        if (!saved || saved.length === 0) {
            this.gridApi?.sizeColumnsToFit();
        }
    }

    /**
     * Parse columnsMap entry: supports old format ["field1","field2"] and new format ["field1:100","field2:150"].
     */
    private parseColumnsShownInOrder(key: string): { fieldOrder: string[]; widthByField: Map<string, number> } {
        const raw = this.persistentUserData?.columnsMap?.[key];
        const fieldOrder: string[] = [];
        const widthByField = new Map<string, number>();
        if (!Array.isArray(raw)) {
            return { fieldOrder, widthByField };
        }
        for (const entry of raw) {
            const s = String(entry).trim();
            const lastColonIdx = s.lastIndexOf(':');
            if (lastColonIdx > 0) {
                const maybeWidth = s.substring(lastColonIdx + 1).trim();
                const widthIsNumeric = /^\d+$/.test(maybeWidth);
                if (widthIsNumeric) {
                    const field = s.substring(0, lastColonIdx).trim();
                    const w = parseInt(maybeWidth, 10);
                    fieldOrder.push(field);
                    if (!isNaN(w) && w > 0) {
                        widthByField.set(field, w);
                    }
                    continue;
                }
            }
            fieldOrder.push(s);
        }
        return { fieldOrder, widthByField };
    }

    private getColumnOrderKeyFromColDef(colDef: ColDef): string | null {
        const persistKey = (colDef as any)?.esciPersistKey;
        if (typeof persistKey === 'string' && persistKey.trim().length > 0) {
            return persistKey;
        }
        if (colDef.field && String(colDef.field).trim().length > 0) {
            return String(colDef.field).trim();
        }
        const colId = colDef.colId;
        if (colId && String(colId).trim().length > 0) {
            return String(colId).trim();
        }
        return null;
    }

    persistColumnData = () => {
        if (this.inSetColumnVisibilityAndOrder || !this.persistentUserData || !this.columnPersistReady) {
            return;
        }

        const key = this.componentName + 'CompColumnsShownInOrder';
        if (!this.persistentUserData.columnsMap) {
            console.warn('No columnsMap found in user data');
            this.persistentUserData.columnsMap = {};
        }
        if (!this.persistentUserData.columnsMap[key]) {
            console.warn('Creating new user persistent data key in userData.columnsMap: ' + key);
        }

        // The order of the columns in this.cols is NOT the displayed order; persist as "field:width" for each column
        const allColumns = this.gridApi.getAllGridColumns();
        this.persistentUserData.columnsMap[key] = allColumns
            .filter(col => !col.getColDef().lockPosition && col.getColDef().colId !== 'Actions')
            .map(col => {
                const colDef = col.getColDef();
                const field = this.getColumnOrderKeyFromColDef(colDef);
                if (!field) {
                    // Skip columns that do not have a stable identifier field
                    return null;
                }
                const width = (col as any).getActualWidth?.() ?? colDef.width ?? colDef.maxWidth;
                return width != null ? `${field}:${Math.round(width)}` : field;
            })
            .filter((entry): entry is string => !!entry);

        // Anytime we update the persistentUserData, set the object in the server DB
        this.userService.savePersistentUserData(this.persistentUserData).subscribe({
            next: () => {
                console.log('Successfully saved user data');
            },
            error: (err: any) => {
                const errorMessage = 'Error saving UserData: ' + (err?.error?.message || err?.message || err);
                console.error(errorMessage);
                this.messageService.processError(errorMessage);
            }
        });
    }

    resetChanges() {
        this.resetting = true;
        this.exporting = true;
        this.messageService.clear();
        this.getData();
    }

    removeSearchTerm(key: string): void {
        if (this.isLockedFilter(key)) {
            return;
        }
        if (key === this.ADVANCED_FILTER) {
            super.removeSearchTerm(key);
            this.removeAdvancedFilter();
        } else {
            // Remove grid filter by paramKey (memberName) so it clears even when base class headerName matching fails
            const paramKey = this.getParamKeyForDisplayName(key);
            if (paramKey != null && this.isGridApiAlive(this.gridApi)) {
                const filterModel = this.gridApi.getFilterModel() || {};
                const newFilterModel = { ...filterModel };
                delete newFilterModel[paramKey];
                this.gridApi.setFilterModel(Object.keys(newFilterModel).length > 0 ? newFilterModel : null);
            } else {
                super.removeSearchTerm(key);
            }
            this.syncUrlAfterFilterChange(key, true);
        }
    }

    removeAllSearchTerms(): void {
        const lockedDefaults = this.readOnlyDefaultFiltersForPage();
        const hadAdvancedFilter = this.advancedFilterCriteria !== null;
        this.advancedFilterCriteria = null;
        this.activeFiltersMap.delete(this.ADVANCED_FILTER);
        if (this.isGridApiAlive(this.gridApi)) {
            const lockedFilterModel = Object.fromEntries(Object.entries(lockedDefaults)
                .map(([memberName, values]) =>
                    [memberName, EsciComponentGridComponent.buildFilterModelEntry(values)]));
            this.gridApi.setFilterModel(Object.keys(lockedFilterModel).length > 0 ? lockedFilterModel : null);
        }
        if (hadAdvancedFilter && !this.serverPaged()) {
            this.refreshGrid(this.unfilteredRowData);
        }
        const reloadServerAdvancedFilter = hadAdvancedFilter && this.serverPaged() &&
            Object.keys(this.initialQueryParams || {}).length === 0;
        this.syncUrlAfterFilterChange(null, false);
        if (reloadServerAdvancedFilter) {
            this.serverPageIndex.set(0);
            this.getServerPage(true);
        }
    }

    /**
     * Updates the URL query params to match the current grid filter state so that
     * the address bar reflects the "complete grid" (or remaining filters) and
     * refresh/bookmarks work as expected. Preserves existing behavior when
     * coming in from the main grid with query params (filtering is unchanged).
     */
    private syncUrlAfterFilterChange(removedDisplayName: string | null, removeOne: boolean): void {
        let newQueryParams: { [key: string]: string | string[] } = {};
        const currentQueryParams = this.initialQueryParams || {};

        if (removeOne && removedDisplayName) {
            const paramKey = this.getParamKeyForDisplayName(removedDisplayName);
            if (paramKey == null) {
                this.updateActiveFilters();
                return;
            }
            // If this filter did not originate from URL query params, avoid reloading data.
            // Reloading from URL-backed params would drop other in-grid filters after a delay.
            if (!Object.prototype.hasOwnProperty.call(currentQueryParams, paramKey)) {
                this.updateActiveFilters();
                return;
            }
            newQueryParams = { ...currentQueryParams };
            delete newQueryParams[paramKey];
        } else {
            // When removing all filters, clear the query params related to the grid.
            // If no URL-driven (server-side) params are currently active, the rows loaded
            // client-side already represent the full unfiltered dataset (default/status filters
            // are applied via gridApi.setFilterModel, not sent to the server) - the caller already
            // cleared that filter model, so there is nothing left to re-fetch. Avoid an unnecessary
            // server round-trip here, since it would also re-run areRowsValid() and re-surface the
            // same data-quality error toasts the user already saw on initial load.
            if (Object.keys(currentQueryParams).length === 0) {
                this.updateActiveFilters();
                return;
            }
            newQueryParams = {};
        }

        this.replaceUrlQueryParams(newQueryParams);
        this.initialQueryParams = { ...newQueryParams };
        this.loading = true;
        this.setRowData([]);
        this.gridOptions.noRowsOverlayComponentParams = this.getNoRowsOverlayParams();
        if (this.gridApi) {
            this.gridApi.showNoRowsOverlay();
        }
        this.getData();
        this.updateActiveFilters();
    }

    /**
     * Replaces the current browser URL's query string without triggering
     * Angular router navigation or re-emitting route observables.
     */
    private replaceUrlQueryParams(params: { [key: string]: string | string[] }): void {
        const searchParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => {
                    if (v != null) {
                        searchParams.append(key, String(v));
                    }
                });
            } else if (value != null) {
                searchParams.set(key, String(value));
            }
        });

        const basePath = window.location.pathname;
        const queryString = searchParams.toString();
        const newUrl = queryString ? `${basePath}?${queryString}` : basePath;

        window.history.replaceState(window.history.state, '', newUrl);
    }
    private getParamKeyForDisplayName(displayName: string): string | null {
        if (!this.objectDescription?.members) {
            return null;
        }
        const member = this.objectDescription.members.find(
            m => (m.displayName || m.memberName) === displayName
        );
        return member?.memberName ?? null;
    }

    removeAdvancedFilter(): void {
        this.activeFiltersMap.delete(this.ADVANCED_FILTER);
        this.activeFiltersArray = Array.from(this.activeFiltersMap.entries())
                                       .map(([key, value]) => ({ key, value }));
        const wasApplied = this.advancedFilterCriteria !== null;
        this.advancedFilterCriteria = null;
        if (!wasApplied) {
            return;
        }
        if (!this.serverPaged()) {
            this.refreshGrid(this.unfilteredRowData);
            return;
        }
        this.serverPageIndex.set(0);
        this.getServerPage();
    }

    /**
     * Creates a "Kit Relationships" column for DiagTestProbe component
     * that opens a modal to manage kit relationships.
     */
    private createKitRelationshipsColumn(): ColDef {
        return {
            headerName: 'Kit Relationships',
            colId: 'kitRelationships',
            width: 150,
            minWidth: 150,
            maxWidth: 200,
            editable: false,
            filter: false,
            sortable: false,
            cellRendererParams: {esciGridLink: 'kitRelationships'},
            cellRenderer: (params: any) => {
                if (!params.data || params.data.id === -1) {
                    return ''; // Don't show link for new/unsaved rows
                }
                const link = document.createElement('a');
                link.href = 'javascript:void(0)';
                link.style.textDecoration = 'underline';
                link.style.color = 'blue';
                link.textContent = 'Open Kits';
                link.title = 'Manage Kit Relationships';
                link.setAttribute('tabindex', '0');
                const activate = () => this.openKitRelationshipsDialog(params.data);
                link.onclick = (ev) => {
                    ev.preventDefault();
                    activate();
                };
                link.addEventListener('keydown', (ev: KeyboardEvent) => {
                    if (ev.key === 'Enter') {
                        ev.preventDefault();
                        ev.stopPropagation();
                        activate();
                    }
                });
                return link;
            }
        };
    }

    /**
     * When a navigable link column (id, kit relationships) has grid focus, Enter activates the same action as click.
     */
    private handleGridLinkCellKeyDown(e: CellKeyDownEvent): void {
        const ke = e.event as KeyboardEvent | undefined;
        if (!ke || ke.key !== 'Enter') {
            return;
        }
        const action = (e.colDef?.cellRendererParams as { esciGridLink?: string } | undefined)?.esciGridLink;
        if (!action || !e.data) {
            return;
        }
        const ae = document.activeElement as HTMLElement | null;
        if (ae?.tagName === 'A' && this.agGridHost?.nativeElement?.contains(ae)) {
            // Focus is on the in-cell link; its keydown handler activates — avoid double-firing
            return;
        }
        ke.preventDefault();
        if (action === 'viewDetails') {
            this.onViewDetailsClick(
                {data: e.data, node: e.node, api: e.api, column: e.column, colDef: e.colDef, context: e.context},
                false
            );
        } else if (action === 'kitRelationships' && e.data.id !== -1) {
            this.openKitRelationshipsDialog(e.data);
        }
    }

    /**
     * Opens the Kit Relationships dialog for a DiagTestProbe or TerminalReleaseTool
     */
    private openKitRelationshipsDialog(rowData: any): void {
        if (this.componentName === 'DiagTestProbe') {
            this.ref = this.dialogService.open(DiagTestProbeRelationshipDialogComponent, {
                header: 'Kit Relationships',
                width: '500px',
                data: {
                    diagTestProbe: rowData,
                    hasAdminPermission: !!this.adminPermission()
                }
            });
        } else if (this.componentName === 'TerminalReleaseTool') {
            this.ref = this.dialogService.open(TerminalReleaseToolRelationshipDialogComponent, {
                header: 'Kit Relationships',
                width: '500px',
                data: {
                    terminalReleaseTool: rowData,
                    hasAdminPermission: !!this.adminPermission()
                }
            });
        }
    }
}
